/*
-------------------------------------------------------------------------------------
Authors         : Alinsonorin, John Myl B., Awi, Joseph Kahl L., Gozon, Daniel Allan
Date Created    : April 30, 2025
Folder          : src/components/homepage
File            : Homepage.tsx
Description     : 
    This component allows the user to access the homepage of the web app.
Copyright © 2025. All rights reserved.
-------------------------------------------------------------------------------------
*/

// Import React Systems
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Modal from "react-modal";

import { PLACEHOLDERS } from "../../utils/placeholders";

// Import Firebase Systems
import { db } from "../../backend/firebaseConfig";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import {
  doc,
  getDoc,
  collection,
  updateDoc,
  onSnapshot,
  deleteField,
  getDocs,
  deleteDoc,
} from "firebase/firestore";

// Import Customized Components
import {
  markNotificationAsRead,
  deleteNotification,
  addNotification,
} from "../../components/functions/notifyComp";
import SplashScreen from "../../components/UI/atoms/splash/SplashScreen";
import ButtonComp from "../../components/UI/atoms/button/Button";

// Import Organisms
import Sidebar from "../../components/UI/organisms/sidebar/Sidebar";
import Header from "../../components/UI/organisms/header/logged-in/HeaderLoggedIn";

// Import Atoms
import Text from "../../components/UI/atoms/text/Text";
import Icon from "../../components/UI/atoms/icon/Icon";
import Fab from "../../components/UI/atoms/fab/Fab";

// Import Molecules
import Notification from "../../components/UI/molecules/notification/Notification";
import DeletionRequestModal from "../../components/UI/molecules/modal/DeletionRequestModal";
import ServiceSelectionModal from "../../components/UI/molecules/modal/ServiceSelectionModal";

// Import Functions
import { fetchUserDetails } from "../../components/functions/fetchUserDetails";
import { notifyFirstUser } from "../../components/functions/notifyFirstUser";
import { redirectIfNotLoggedIn } from "../../components/functions/redirectIfNotLoggedIn";
import Select from "../../components/UI/atoms/select/Select";


// Imports MUI Components
import {
  Box,
  Button,
  Stack,
  Typography,
  Grid,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  TextField,
  Drawer,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TableBody,
  TableFooter,
  Tooltip,
  InputLabel,
  MenuItem,
  FormControl,
  Select as Selected,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Pagination,
  Snackbar as MuiSnackbar,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import { tooltipClasses } from "@mui/material/Tooltip";

// Import Assets
import notification from "../../assets/notification.png";
import calendar from "../../assets/icons/calendar_default.png";
import clock from "../../assets/clock.png";
import status from "../../assets/status.png";
import check from "../../assets/check.png";
import appointments from "../../assets/icons/appointment.png";
import money from "../../assets/icons/money.png";
import patients from "../../assets/icons/patients.png";
import pending from "../../assets/icons/pending.png";

import AlertShortMessage from "../../components/UI/molecules/alert/AlertShortMessage";
import Snackbar from "../../components/UI/atoms/snackbar/Snackbar";
import MedicalHistory from "../../components/UI/organisms/history/MedicalHistoryPatient";

import "./styles.css"
import Empty from "../../components/UI/molecules/empty/Empty";
import ClinicStaffDashboardCalendar from "../../components/appointment/ClinicStaffDashboardCalendar";

function formatDateLong(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  const date = new Date(year, month - 1, day);
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  return `${monthNames[date.getMonth()]} ${day}, ${year}`;
}


function formatDuration(minutes) {
  if (minutes === null || minutes === undefined || minutes === "") return "N/A";
  const m = Number(minutes);
  if (isNaN(m)) return `${minutes} minutes`;
  if (m < 60) return `${m} minute${m !== 1 ? "s" : ""}`;
  const hours = Math.floor(m / 60);
  const mins = m % 60;
  if (mins === 0) return `${hours} hour${hours !== 1 ? "s" : ""}`;
  return `${hours} hour${hours !== 1 ? "s" : ""} and ${mins} minute${mins !== 1 ? "s" : ""}`;
}

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 200,
    padding: "10px",
    fontSize: "12px",
  },
});

const CustomWidthTooltipHistory = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 200,
    padding: "10px",
    fontSize: "12px",
  },
});

const Dashboard = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const cancelLinkHandledRef = useRef(false);

  const clearCancelQueryParams = () => {
    cancelLinkHandledRef.current = true;
    const params = new URLSearchParams(searchParams);
    [
      'isCanceling',
      'email',
      'appointmentId',
      'apptId',
      'date',
      'apptDate',
      'dateKey',
      'dateISO',
      'startTime',
      'apptStart',
      'endTime',
      'apptEnd',
      'dentist',
      'apptDentist',
      'services',
      'apptServices'
    ].forEach((key) => params.delete(key));
    setSearchParams(params, { replace: true });
  };

  // Government ID options for dropdown
  const governmentIdOptions = [
    { id: 1, name: "Philippine Identification Card (PhilSys ID / National ID)" },
    { id: 2, name: "Philippine Passport" },
    { id: 3, name: "Driver's License (LTO)" },
    { id: 4, name: "Unified Multi-Purpose Identification Card (UMID)" },
    { id: 5, name: "Social Security System Identification Card (SSS ID)" },
    { id: 6, name: "Government Service Insurance System Electronic Card (GSIS eCard / GSIS ID)" },
    { id: 7, name: "Professional Regulation Commission Identification Card (PRC ID)" },
    { id: 8, name: "Philippine Postal Identification Card (Postal ID)" },
    { id: 9, name: "Voter's Identification Card / Voter's Certification (Commission on Elections or COMELEC)" },
    { id: 10, name: "National Bureau of Investigation Clearance (NBI Clearance)" },
    { id: 11, name: "Police Clearance Certificate" },
    { id: 12, name: "Senior Citizen Identification Card (Senior Citizen ID)" },
    { id: 13, name: "Person with Disability Identification Card (PWD ID)" },
    { id: 14, name: "Overseas Workers Welfare Administration Identification Card (OWWA ID / OWWA eCard)" },
    { id: 15, name: "Overseas Filipino Worker Identification Card (OFW ID)" },
    { id: 16, name: "Seafarer's Identification and Record Book (SIRB / Seaman's Book)" },
    { id: 17, name: "Alien Certificate of Registration Identity Card (ACR I-Card)" },
    { id: 18, name: "Firearms License Identification Card (Philippine National Police or PNP Firearms ID)" },
    { id: 19, name: "Armed Forces of the Philippines Identification Card (AFP ID)" },
    { id: 20, name: "Philippine Veterans Affairs Office Identification Card (PVAO ID)" },
    { id: 21, name: "Barangay Identification Card (Barangay ID)" },
    { id: 22, name: "Philippine Health Insurance Corporation Identification Card (PhilHealth ID)" },
    { id: 23, name: "Taxpayer Identification Number Identification Card (TIN ID / BIR ID)" },
    { id: 24, name: "Student Identification Card (Student ID)" },
    { id: 25, name: "Employee Identification Card (Employee ID)" }
  ];
  

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [userDataLoading, setUserDataLoading] = useState(true); // Track if user data is fully loaded

  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // pending appointments modal state
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [pendingModalShown, setPendingModalShown] = useState(false);
  const [currentUserUid, setCurrentUserUid] = useState(null);
  const [totalPendingAppointments, setTotalPendingAppointments] = useState(0);
  const [pendingRangeType, setPendingRangeType] = useState("30");

  // widget states
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [totalIncome, setTotalIncome] = useState(0);
  const [newPatients, setNewPatients] = useState(0);
  const [returningPatients, setReturningPatients] = useState(0);
  const [popularServices, setPopularServices] = useState([]);
  const [appointmentsDaysRange, setAppointmentsDaysRange] = useState(30);
  const [incomeDaysRange, setIncomeDaysRange] = useState(30);
  const [patientsDaysRange, setPatientsDaysRange] = useState(30);
  const [latestDaysRange, setLatestDaysRange] = useState(30);
  const [popularDaysRange, setPopularDaysRange] = useState(30);
  const [appointmentsRangeType, setAppointmentsRangeType] = useState("30");
  const [incomeRangeType, setIncomeRangeType] = useState("30");
  const [patientsRangeType, setPatientsRangeType] = useState("30");
  const [latestRangeType, setLatestRangeType] = useState("30");
  const [popularRangeType, setPopularRangeType] = useState("30");
  const [activeStatsWidget, setActiveStatsWidget] = useState("appointments");
  const [widgetsTotalAppointments, setWidgetsTotalAppointments] =
    React.useState("");
  
  // Latest appointments dentist filter
  const [dentistsList, setDentistsList] = useState([]);
  const [selectedLatestDentist, setSelectedLatestDentist] = useState("all");


  // Handler to read notification button result


  const [treatmentHistory, setTreatmentHistory] = useState([]);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [latestPatientAppointments, setLatestPatientAppointments] = useState(
    []
  );
  const [latestAppointments, setLatestAppointments] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showMedicalHistoryModal, setShowMedicalHistoryModal] = useState(false);
  const medicalHistoryRef = useRef(null);
  const [showMedicalHistorySnackbar, setShowMedicalHistorySnackbar] = useState(false);
  const [medicalHistorySearchTerm, setMedicalHistorySearchTerm] = useState("");
  const NOTIFICATIONS_PAGE_SIZE = 10;
  const [notifPage, setNotifPage] = useState(1);
  // reset to page 1 when modal opens
  useEffect(() => {
    if (showNotificationsModal) setNotifPage(1);
  }, [showNotificationsModal]);

  // reset search when modal closes
  useEffect(() => {
    if (!showMedicalHistoryModal) setMedicalHistorySearchTerm("");
  }, [showMedicalHistoryModal]);
  
  // Handle isCanceling query parameter - auto-open cancel appointment modal
  useEffect(() => {
    const handleCancelFromParams = async () => {
      const isCanceling = searchParams.get('isCanceling');
      const emailParam = searchParams.get('email');
      // If we have an email param, merge it into userDetails state so it
      // is available once userDetails is loaded. Do not remove the query
      // params here — we may need to wait for auth/userDetails to be loaded.
      if (emailParam) {
        setUserDetails((prev) => ({ ...(prev || {}), email: emailParam }));
      }

      if (isCanceling !== 'true' || cancelLinkHandledRef.current) return;

      // Prefer the authenticated user's email when available
      const authEmail = auth?.currentUser?.email || null;

      // If neither auth email nor userDetails are ready yet, wait and retry
      if (!emailParam) {
        clearCancelQueryParams();
        return;
      }

      if (!authEmail && (!userDetails || !userDetails.email)) {
        // Still loading user details/auth state — return early and do NOT
        // clear the URL so this effect can re-run when state arrives.
        console.log('Cancel flow: waiting for auth/userDetails to load', { emailParam, userDetails });
        return;
      }

      const resolvedEmail = authEmail || (userDetails && userDetails.email);
      if (emailParam !== resolvedEmail) {
        console.warn('Email parameter does not match current user email', { emailParam, resolvedEmail });
        clearCancelQueryParams();
        return;
      }

      // Extract optional appointment fields from URL (support legacy names)
      const apptId = searchParams.get('appointmentId') || searchParams.get('apptId');
      const apptDate = searchParams.get('date') || searchParams.get('apptDate');
      const apptDateKey = searchParams.get('dateKey') || searchParams.get('dateISO') || null;
      const apptStart = searchParams.get('startTime') || searchParams.get('apptStart');
      const apptEnd = searchParams.get('endTime') || searchParams.get('apptEnd');
      const apptDentist = searchParams.get('dentist') || searchParams.get('apptDentist');
      const apptServices = searchParams.get('services') || searchParams.get('apptServices'); // comma separated or encoded

      cancelAppointmentParamsRef.current = {
        apptId,
        apptDate,
        apptDateKey,
        apptStart,
        apptEnd,
        apptDentist,
        apptServices,
        emailParam,
      };

      // Prepare selectedAppointment object with available values
      let prefill = {
        id: apptId || null,
        date: apptDate || null,
        startTime: apptStart || null,
        endTime: apptEnd || null,
        dentist: apptDentist ? decodeURIComponent(apptDentist) : null,
        services: apptServices ? decodeURIComponent(apptServices) : null,
      };

      // If we don't have an appointment ID but have date + startTime, try to resolve the appointment
      if (!prefill.id && prefill.date && prefill.startTime && currentUserUid) {
        try {
          // Prefer a normalized date key if available (YYYY-MM-DD). If only a human-readable
          // date was provided, attempt to parse it into an ISO date string.
          const normalizeDateKey = (d) => {
            if (!d) return null;
            if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
            const parsed = new Date(d);
            if (!isNaN(parsed.getTime())) {
              return parsed.toISOString().split('T')[0];
            }
            return null;
          };

          const docKey = prefill.dateKey || normalizeDateKey(prefill.date) || prefill.date;
          const apptDocRef = doc(db, 'appointments', docKey);
          const apptDoc = await getDoc(apptDocRef);
          if (apptDoc.exists()) {
            const data = apptDoc.data();
            for (const [key, appt] of Object.entries(data)) {
              const matchStart = (appt.startTime || appt.time || '').toString() === prefill.startTime.toString();
              const matchPatient = appt.patientId === currentUserUid;
              if (matchStart && matchPatient) {
                prefill.id = key;
                // fill missing fields from firestore record
                prefill.endTime = prefill.endTime || appt.endTime || null;
                prefill.dentist = prefill.dentist || appt.dentist || null;
                prefill.services = prefill.services || appt.services || null;
                break;
              }
            }
          }
        } catch (err) {
          console.error('Error resolving appointment from URL params:', err);
        }
      }

      // Normalize services: keep arrays as-is, otherwise split comma string
      if (typeof prefill.services === 'string') {
        try {
          // try parse JSON array first
          const parsed = JSON.parse(prefill.services);
          prefill.services = parsed;
        } catch (e) {
          prefill.services = prefill.services
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        }
      }

      // Set the selected appointment and open the modal
      setSelectedAppointment((prev) => ({ ...prev, ...prefill }));
      setShowCancelAppointmentModal(true);
      cancelLinkHandledRef.current = true;

      // Clean up the cancel link query params
      clearCancelQueryParams();
    };

    handleCancelFromParams();
  }, [searchParams, userDetails, currentUserUid]);
  
  // Confirmation modal state for notification deletion
  const [showDeleteNotificationConfirm, setShowDeleteNotificationConfirm] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [isDeleteAll, setIsDeleteAll] = useState(false);
  
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const cancelAppointmentParamsRef = useRef({});
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [showCancelAppointmentModal, setShowCancelAppointmentModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isCancelAppointmentLoading, setIsCancelAppointmentLoading] = useState(false);
  const [isKeepAppointmentLoading, setIsKeepAppointmentLoading] = useState(false);
  const [isCancelAppointmentButtonLoading, setIsCancelAppointmentButtonLoading] = useState(false);
  const [treatmentHistoryLoading, setTreatmentHistoryLoading] = useState(true);
  const [insuranceMessage, setInsuranceMessage] = useState("");

  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertImage, setAlertImage] = useState("");
    const [user, setCurrentUser] = useState(null);
  const [alertButton, setAlertButton] = useState("");
  const [alertButtonType, setAlertButtonType] = useState("false");
  const [alertLink, setAlertLink] = useState("");

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState("");

  const [iconSrc, setIconSrc] = useState(
    require("../../assets/icons/help.png")
  );
  const [iconSrcHistory, setIconSrcHistory] = useState(
    require("../../assets/icons/help.png")
  );

  // Deletion request modal state
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [selectedDeletionRequest, setSelectedDeletionRequest] = useState(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Service selection modal state (for first-time dentist login)
  const [showServiceSelectionModal, setShowServiceSelectionModal] = useState(false);

  // Calculate unread notifications count (excluding welcome notifications)
  const unreadCount = notifications.filter(
    (notification) => 
      !notification.read &&
      !(notification.message.toLowerCase().includes('welcome') &&
        notification.message.toLowerCase().includes('first notification'))
  ).length;
  // const unreadCount = 100;

  // Function to fetch completed appointments (treatment history)


  const fetchLatestPatientAppointments = (uid) => {
    // Only fetch if user is authenticated
    if (!uid) {
      return;
    }

    // Get all appointment documents (dates)
    const appointmentsRef = collection(db, "appointments");

    onSnapshot(
      appointmentsRef,
      (snapshot) => {
        let upcomingAppointments = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

        snapshot.forEach((dateDoc) => {
          const dateData = dateDoc.data();
          // Iterate through all appointments for this date
          Object.entries(dateData).forEach(([appointmentId, appointment]) => {
            const appointmentDate = new Date(appointment.date);
            appointmentDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

            if (
              appointment.patientId === uid &&
              (appointment.status === "Pending" ||
                appointment.status === "Approved" ||
                appointment.status === "Confirmed")
              && appointmentDate >= today // Only include future or today's appointments
            ) {
              upcomingAppointments.push({ id: appointmentId, ...appointment });
            }
          });
        });

       // Sort by date descending (latest on top) and limit to the top 3
       const sortedAppointments = upcomingAppointments
          .sort((a, b) => parseAppointmentDateTime(a) - parseAppointmentDateTime(b))
          .slice(0, 3);

        setLatestPatientAppointments(sortedAppointments);
      },
      (error) => {
        if (error.code !== "permission-denied") {
          console.error("Dashboard: Error fetching latest appointments:", error);
          console.error("Failed to fetch latest patient appointments");
        }
        setLatestPatientAppointments([]);
      }
    );
  };

  // Mark notification as read
  /**
   * Marks a notification as read.
   * @param {string} notificationId - The notification ID.
   */
  const handleMarkAsRead = (notificationId) => {
    if (!auth.currentUser || !notificationId) return;
    markNotificationAsRead(auth.currentUser, notificationId);
    setNotifications((prevNotifications) =>
      prevNotifications.map((notification) =>
        notification.id === notificationId
          ? { ...notification, read: true }
          : notification
      )
    );
  };

  // Handle deletion request click
  const handleDeletionRequestClick = (notification) => {
    if (notification.type === 'account-deletion-request' && userDetails?.role === 'DentistOwner') {
      setSelectedDeletionRequest(notification);
      setShowDeletionModal(true);
      setShowNotificationsModal(false);
    }
  };

  // Handle approve deletion
  const handleApproveDeletion = async (requestData) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const idToken = await user.getIdToken();
      const response = await fetch('/api/handle-deletion-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          requestId: requestData.deletionRequestId,
          action: 'approve',
          approverUid: userDetails?.uid,
          approverName: `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to approve deletion');
      }

      setSnackbarMessage('Account deletion approved successfully');
      setShowSuccessSnackbar(true);
      setShowDeletionModal(false);
      
      // Mark notification as read
      handleMarkAsRead(selectedDeletionRequest.id);
    } catch (error) {
      console.error('Error approving deletion:', error);
      setSnackbarMessage('Failed to approve deletion request');
      setShowErrorSnackbar(true);
    }
  };

  // Handle reject deletion
  const handleRejectDeletion = async (requestData) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const idToken = await user.getIdToken();
      const response = await fetch('/api/handle-deletion-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          requestId: requestData.deletionRequestId,
          action: 'reject',
          approverUid: userDetails?.uid,
          approverName: `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reject deletion');
      }

      setSnackbarMessage('Account deletion request rejected');
      setShowSuccessSnackbar(true);
      setShowDeletionModal(false);
      
      // Mark notification as read
      handleMarkAsRead(selectedDeletionRequest.id);
    } catch (error) {
      console.error('Error rejecting deletion:', error);
      setSnackbarMessage('Failed to reject deletion request');
      setShowErrorSnackbar(true);
    }
  };

  // Function to handle appointment actions
  /**
   * Handles appointment actions (view, modify, etc).
   * @param {object} appointment - The appointment object.
   * @param {string} action - The action to perform.
   */
  const handleAppointmentAction = (appointment, action) => {
    setSelectedAppointment(appointment);
    setDropdownOpen(null);

    if (action === "view") {
      setShowAppointmentModal(true);
    }
  };

  const handleMedicalHistorySearchChange = (e) => {
    setMedicalHistorySearchTerm(e.target.value);
  };

  /**
   * Formats a time string to 12-hour format with AM/PM.
   * @param {string} timeStr - The time string to format.
   * @returns {string} The formatted time.
   */
  // Function to toggle dropdown
  const toggleDropdown = (appointmentId) => {
    setDropdownOpen(dropdownOpen === appointmentId ? null : appointmentId);
  };

  /**
   * Formats a time string to 12-hour format with AM/PM.
   * @param {string} timeStr - The time string to format.
   * @returns {string} The formatted time.
   */
  function formatTimeConsistent(timeStr) {
    if (!timeStr) return "N/A";
    // If it's already in 12-hour format with AM/PM, return as is
    if (timeStr.includes("AM") || timeStr.includes("PM")) {
      return timeStr;
    }
    // If it's in 24-hour format, convert to 12-hour
    if (timeStr.includes(":")) {
      const [hours, minutes] = timeStr.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      const displayMinutes = minutes.padStart(2, "0");
      return `${displayHour}:${displayMinutes} ${ampm}`;
    }
    return timeStr;
  }

  // Fetch all dentists for dropdown
  useEffect(() => {
    if (!isAuthenticated || !userDetails) return;
    
    const userRole = userDetails.role || userDetails.userType;
    // Only fetch dentists for DentistOwner and ClinicStaff
    if (userRole !== "DentistOwner" && userRole !== "ClinicStaff") return;
    
    const fetchDentists = async () => {
      try {
        const usersRef = collection(db, "users");
        const querySnapshot = await getDocs(usersRef);
        const dentists = [];
        
        querySnapshot.forEach((doc) => {
          const userData = doc.data();
          const role = userData.role || userData.userType;
          const isDeleted =
            userData.accountDeleted === true ||
            userData.accountDeleted === "true" ||
            (typeof userData.accountStatus === "string" &&
              userData.accountStatus.toLowerCase() === "deleted");
          if (isDeleted) return;
          if (role === "DentistOwner" || role === "AssociateDentist") {
            const firstName = (userData.firstName || "").trim();
            const lastName = (userData.lastName || "").trim();
            const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
            if (fullName) {
              dentists.push({
                id: doc.id,
                name: `Dr. ${fullName}`,
                firstName,
                lastName
              });
            }
          }
        });

        // Guard against duplicate labels from legacy duplicate accounts.
        const uniqueByName = Array.from(
          new Map(dentists.map((dentist) => [dentist.name, dentist])).values()
        );
        setDentistsList(uniqueByName);
      } catch (error) {
        console.error("Error fetching dentists:", error);
      }
    };
    
    fetchDentists();
  }, [isAuthenticated, userDetails]);

  // MASTER Authentication and Data Listener - Consolidated from 7 separate listeners
  useEffect(() => {
    let unsubscribeNotifications = null;
    let unsubscribeCompletedAppointments = null;
    let unsubscribeNextAppointment = null;
    
    // Track when both data sources are loaded
    let treatmentHistoryLoaded = false;
    let appointmentsLoaded = false;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Always clear previous user data and show loading state on auth change
        // This prevents showing stale data from previous sessions
        const previousUid = currentUserUid;
        
        // Clear all data immediately when user changes or on first load
        if (previousUid !== user.uid || !previousUid) {
          setUserDetails(null);
          setNextAppointment(null);
          setLatestPatientAppointments([]);
          setTreatmentHistory([]);
        }
        
        // Always set loading to true until we confirm current user's data is loaded
        setUserDataLoading(true);
        setTreatmentHistoryLoading(true);
        
        // Reset loading trackers for new user
        treatmentHistoryLoaded = false;
        appointmentsLoaded = false;
        
        // Set authentication state
        setIsAuthenticated(true);
        setAuthLoading(false);
        
        // Store current UID to verify data belongs to this user
        const currentUid = user.uid;
        setCurrentUserUid(currentUid);
        
        // Check session for modal state
        const shown = sessionStorage.getItem(`pendingModalShown_${user.uid}`) === "true";
        setPendingModalShown(shown);
        
        // Fetch user details
        fetchUserDetails(setUserDetails);
        notifyFirstUser();
        
        // Fetch latest patient appointments
        fetchLatestPatientAppointments(user.uid);
        
        // Fetch user details for sidebar
        const userRef = doc(db, "users", user.uid);
        const snapshot = await getDoc(userRef);
        if (snapshot.exists()) {
          const userData = snapshot.data();
          const isDentist = userData.userType === "DentistOwner" || userData.role === "DentistOwner" || 
                           userData.userType === "AssociateDentist" || userData.role === "AssociateDentist";
          
          if (isDentist) {
            setUserDetails({ id: user.uid, ...userData });
            // Check if dentist has selected their services (only show once - on first login)
            // Once hasSelectedServices is true in Firestore, it won't show again
            if (userData.hasSelectedServices === false && !showServiceSelectionModal) {
              // Show service selection modal for first-time dentist login
              setShowServiceSelectionModal(true);
            } else if (userData.hasSelectedServices === true && showServiceSelectionModal) {
              // If services are already selected, make sure modal is closed
              setShowServiceSelectionModal(false);
            }
          }
          // Set user details for patients as well
          if (userData.userType === "Patient" || userData.role === "Patient") {
            setUserDetails({ id: user.uid, ...userData });
            // Check if medical history hasn't been completed (only show once ever)
            // Once healthHistory exists in Firestore, it won't show again
            // Only show if healthHistory doesn't exist AND modal isn't already showing
            if (!userData.healthHistory && !showMedicalHistoryModal) {
              // Show medical history modal on first sign-in only
              setShowMedicalHistoryModal(true);
            } else if (userData.healthHistory && showMedicalHistoryModal) {
              // If healthHistory exists, make sure modal is closed
              setShowMedicalHistoryModal(false);
            }
          }
        }
        
        // Fetch treatment history and next appointment with proper cleanup
        const appointmentsRef = collection(db, "appointments");

        // Completed appointments listener
        unsubscribeCompletedAppointments = onSnapshot(
          appointmentsRef,
          async (snapshot) => {

            // Collect completed appointments first
            const appointmentsToProcess = [];
            snapshot.forEach((dateDoc) => {
              const dateData = dateDoc.data();
              Object.entries(dateData).forEach(
                ([appointmentId, appointment]) => {
                  if (
                    (appointment.patientId === user.uid ||
                      appointment.id === user.uid) &&
                    appointment.status === "Completed"
                  ) {
                    appointmentsToProcess.push({
                      id: appointmentId,
                      ...appointment,
                      services: Array.isArray(appointment.services)
                        ? appointment.services
                        : [appointment.services || "Service"],
                    });
                  }
                }
              );
            });

            // Fetch dentist profile pictures for completed appointments
            const appointmentsWithDentistInfo = await Promise.all(
              appointmentsToProcess.map(async (appointment) => {
                if (appointment.dentistId) {
                  try {
                    const dentistRef = doc(db, "users", appointment.dentistId);
                    const dentistSnapshot = await getDoc(dentistRef);
                    if (dentistSnapshot.exists()) {
                      const dentistData = dentistSnapshot.data();
                      return {
                        ...appointment,
                        dentistProfilePicture:
                          dentistData.profilePictureUrl || null,
                      };
                    }
                  } catch (error) {
                    console.error(
                      "Error fetching dentist profile for completed appointment:",
                      error
                    );
                  }
                }
                return appointment;
              })
            );

            const sortedAppointments = appointmentsWithDentistInfo.sort(
              (a, b) => new Date(b.date) - new Date(a.date)
            );
            // Verify data belongs to current user before setting
            const appointmentsForUser = sortedAppointments.filter(appt => 
              appt.patientId === currentUid || appt.userId === currentUid
            );
            setTreatmentHistory(appointmentsForUser.slice(0, 3));
            treatmentHistoryLoaded = true;
            
            // Only set loading to false when BOTH data sources are loaded
            if (appointmentsLoaded && auth.currentUser?.uid === currentUid) {
              setTimeout(() => {
                if (auth.currentUser?.uid === currentUid) {
                  setTreatmentHistoryLoading(false);
                  setUserDataLoading(false);
                }
              }, 100);
            }
            // Keep treatmentHistoryLoading true if appointments aren't loaded yet
          },
          (error) => {
            if (error?.code !== "permission-denied") {
              console.error("Dashboard: Error fetching appointments:", error);
            }
            setTreatmentHistory([]);
            treatmentHistoryLoaded = true;
            
            // Only set loading to false when BOTH data sources are loaded (even on error)
            if (appointmentsLoaded && auth.currentUser?.uid === currentUid) {
              setTimeout(() => {
                if (auth.currentUser?.uid === currentUid) {
                  setTreatmentHistoryLoading(false);
                  setUserDataLoading(false);
                }
              }, 100);
            }
            // Keep treatmentHistoryLoading true if appointments aren't loaded yet
          }
        );

        // Next appointment listener
        unsubscribeNextAppointment = onSnapshot(
          appointmentsRef,
          async (snapshot) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Collect appointments first
            const appointmentsToProcess = [];
            snapshot.forEach((dateDoc) => {
              const dateData = dateDoc.data();
              Object.entries(dateData).forEach(
                ([appointmentId, appointment]) => {
                  const appointmentDate = new Date(appointment.date);
                  appointmentDate.setHours(0, 0, 0, 0);
                  if (
                    (appointment.patientId === user.uid ||
                      appointment.id === user.uid) &&
                    (appointment.status === "Scheduled" ||
                      appointment.status === "Pending" ||
                      appointment.status === "Confirmed")
                    && appointmentDate >= today
                  ) {
                    appointmentsToProcess.push({
                      id: appointmentId,
                      ...appointment,
                    });
                  }
                }
              );
            });

            // Fetch dentist profile pictures for each appointment
            const appointmentsWithDentistInfo = await Promise.all(
              appointmentsToProcess.map(async (appointment) => {
                if (appointment.dentistId) {
                  try {
                    const dentistRef = doc(db, "users", appointment.dentistId);
                    const dentistSnapshot = await getDoc(dentistRef);
                    if (dentistSnapshot.exists()) {
                      const dentistData = dentistSnapshot.data();
                      return {
                        ...appointment,
                        dentistProfilePicture:
                          dentistData.profilePictureUrl || null,
                      };
                    }
                  } catch (error) {
                    console.error("Error fetching dentist profile:", error);
                  }
                }
                return appointment;
              })
            );

           // Sort descending so the latest date appears on top
            const sortedUpcoming = appointmentsWithDentistInfo.sort(
            (a, b) => parseAppointmentDateTime(a) - parseAppointmentDateTime(b)
           );
            // Verify appointments belong to current user before setting
            const userAppointments = sortedUpcoming.filter(appt => 
              appt.patientId === currentUid || appt.userId === currentUid
            );
            
            setNextAppointment(userAppointments[0] || null);
            setLatestPatientAppointments(userAppointments);
            appointmentsLoaded = true;
            
            // Check if both data sources are loaded and set loading states to false
            if (treatmentHistoryLoaded && auth.currentUser?.uid === currentUid) {
              setTimeout(() => {
                if (auth.currentUser?.uid === currentUid) {
                  setTreatmentHistoryLoading(false);
                  setUserDataLoading(false);
                }
              }, 100);
            }
          }
        );

        // Notifications listener
        const notificationsRef = collection(
          db,
          "notifications",
          user.uid,
          "userNotifications"
        );
        unsubscribeNotifications = onSnapshot(
          notificationsRef,
          (snapshot) => {
            const fetchedNotifications = [];
            snapshot.forEach((doc) => {
              fetchedNotifications.push({
                id: doc.id,
                ...doc.data(),
              });
            });
            setNotifications(fetchedNotifications);
          },
          (error) => {
            // Silently handle permission errors during sign-out
            if (error.code === "permission-denied") {
              return;
            }
            console.error("Error fetching notifications:", error);
          }
        );
      } else {
        // User signed out - reset all state
        setIsAuthenticated(false);
        setAuthLoading(false);
        setTreatmentHistoryLoading(true);
        setTreatmentHistory([]);
        setNextAppointment(null);
        setLatestPatientAppointments([]);
        setNotifications([]);
        setUserDetails(null);
        setCurrentUserUid(null);
        setPendingModalShown(false);
        
        // Clear session storage
        if (currentUserUid) {
          sessionStorage.removeItem(`pendingModalShown_${currentUserUid}`);
        }
        
        // Redirect to sign-in if not authenticated
        redirectIfNotLoggedIn(setShowCustomAlert, navigate);
        
        // Unsubscribe from all listeners if active
        if (unsubscribeNotifications) {
          unsubscribeNotifications();
          unsubscribeNotifications = null;
        }
        if (unsubscribeCompletedAppointments) {
          unsubscribeCompletedAppointments();
          unsubscribeCompletedAppointments = null;
        }
        if (unsubscribeNextAppointment) {
          unsubscribeNextAppointment();
          unsubscribeNextAppointment = null;
        }
      }
    });

    return () => {
      unsubscribe();
      if (unsubscribeNotifications) {
        unsubscribeNotifications();
      }
      if (unsubscribeCompletedAppointments) {
        unsubscribeCompletedAppointments();
      }
      if (unsubscribeNextAppointment) {
        unsubscribeNextAppointment();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, navigate]);

  function parseAppointmentDateTime(appt) {
  // date source: appt.date or appt._dateKey (YYYY-MM-DD)
  const dateStr = appt.date || appt._dateKey || "";
  const dateParts = String(dateStr).split("-");
  let year = 1970, month = 1, day = 1;
  if (dateParts.length === 3) {
    year = Number(dateParts[0]) || year;
    month = Number(dateParts[1]) || month;
    day = Number(dateParts[2]) || day;
  } else {
    const parsed = new Date(dateStr);
    if (!isNaN(parsed)) {
      year = parsed.getFullYear();
      month = parsed.getMonth() + 1;
      day = parsed.getDate();
    }
  }

  // time source: startTime, time, or empty
  const timeStrRaw = String(appt.startTime || appt.time || "").trim();
  // take first part if it's a range "08:00 - 09:00"
  const timeStr = timeStrRaw.includes("-")
    ? timeStrRaw.split("-")[0].trim()
    : timeStrRaw;

  // parse time (supports "HH:mm", "H:mm AM/PM", "H AM/PM", "HHmm")
  let hours = 0, minutes = 0;
  if (timeStr) {
    const ampmMatch = /(\d{1,2}):?(\d{0,2})\s*(AM|PM)/i.exec(timeStr);
    if (ampmMatch) {
      hours = parseInt(ampmMatch[1], 10) || 0;
      minutes = parseInt(ampmMatch[2] || "0", 10) || 0;
      const isPM = /PM/i.test(ampmMatch[3]);
      if (isPM && hours < 12) hours += 12;
      if (!isPM && hours === 12) hours = 0;
    } else {
      const hhmm = /(\d{1,2}):(\d{2})/.exec(timeStr);
      if (hhmm) {
        hours = parseInt(hhmm[1], 10) || 0;
        minutes = parseInt(hhmm[2], 10) || 0;
      } else {
        const numeric = /^\d{1,4}$/.exec(timeStr.replace(/:/g, ""));
        if (numeric) {
          const s = numeric[0];
          if (s.length <= 2) {
            hours = parseInt(s, 10) || 0;
            minutes = 0;
          } else {
            hours = parseInt(s.slice(0, s.length - 2), 10) || 0;
            minutes = parseInt(s.slice(-2), 10) || 0;
          }
        }
      }
    }
  }

  const dt = new Date(year, (month || 1) - 1, day || 1, hours || 0, minutes || 0, 0, 0);
  return dt;
}

function parseNotificationDate(notif) {
  if (!notif) return new Date(0);
  const ts = notif.createdAt || notif.date || notif.timestamp || notif.timeStamp;
  if (!ts) return new Date(0);

  if (typeof ts.toDate === "function") {
    try { return ts.toDate(); } catch (e) { }
  }

  if (typeof ts === "object" && ts.seconds) {
    return new Date(ts.seconds * 1000);
  }

  if (typeof ts === "number") {
    return ts > 1e12 ? new Date(ts) : new Date(ts * 1000);
  }

  try {
    const d = new Date(String(ts));
    if (!isNaN(d)) return d;
  } catch (e) {}

  return new Date(0);
}

function formatNotificationDate(notif) {
  const dt = parseNotificationDate(notif);
  if (!dt || isNaN(dt.getTime())) return "";
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

function formatNotificationTime(notif) {
  const dt = parseNotificationDate(notif);
  if (!dt || isNaN(dt.getTime())) return "";
  return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function normalizeMessageDates(msg) {
  if (!msg) return msg;
  return String(msg).replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (_m, y, mo, d) => `${mo}-${d}-${y}`);
}

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest(".dropdown-container")) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Removed: Duplicate listener - now handled in master listener above

  // Removed: Duplicate listener - now handled in master listener above
  // Removed: Duplicate listener - notifications now handled in master listener above

  // Removed: Duplicate listener - user details now handled in master listener above

  // Removed: Duplicate listener - modal state now handled in master listener above

  // --- Fetch pending appointments for the next 3 days and show modal after sign in ---
  useEffect(() => {
    if (!currentUserUid || pendingModalShown) return;

    const fetchPending = async () => {
      const today = new Date();
      const next3Days = [];
      for (let i = 0; i < 3; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        next3Days.push(`${yyyy}-${mm}-${dd}`);
      }

      let allPending = [];
      for (const dateStr of next3Days) {
        try {
          const apptDoc = await getDoc(doc(db, "appointments", dateStr));
          if (apptDoc.exists()) {
            const data = apptDoc.data();
            Object.entries(data).forEach(([id, appt]) => {
              if (appt.status === "Pending" || appt.status === "New") {
                allPending.push({
                  ...appt,
                  id,
                  date: dateStr,
                });
              }
            });
          }
        } catch (err) {
          // ignore missing docs
        }
      }
      setPendingAppointments(allPending);
      if (allPending.length > 0) {
        setShowPendingModal(true);
        setPendingModalShown(true);
        sessionStorage.setItem(`pendingModalShown_${currentUserUid}`, "true");
      }
    };

    fetchPending();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserUid, pendingModalShown]);

  // When modal is closed, keep it closed until next sign-in
  const handleClosePendingModal = () => {
    setShowPendingModal(false);
    setPendingModalShown(true);
    if (currentUserUid) {
      sessionStorage.setItem(`pendingModalShown_${currentUserUid}`, "true");
    }
  };

  // Delete notification - show confirmation first
  const handleDeleteNotification = (notificationId) => {
    setNotificationToDelete(notificationId);
    setIsDeleteAll(false);
    setShowDeleteNotificationConfirm(true);
  };

  // Confirm and delete single notification
  const handleConfirmDeleteNotification = () => {
    if (!auth.currentUser || !notificationToDelete) return;
    deleteNotification(auth.currentUser, notificationToDelete);
    setNotifications((prevNotifications) =>
      prevNotifications.filter(
        (notification) => notification.id !== notificationToDelete
      )
    );
    setShowDeleteNotificationConfirm(false);
    setNotificationToDelete(null);
  };

  // Delete all notifications - show confirmation first
  const handleDeleteAllNotifications = () => {
    setNotificationToDelete(null);
    setIsDeleteAll(true);
    setShowDeleteNotificationConfirm(true);
  };

  // Confirm and delete all notifications
  const handleConfirmDeleteAllNotifications = () => {
    if (!auth.currentUser) return;
    notifications.forEach((notification) => {
      deleteNotification(auth.currentUser, notification.id);
    });
    // Clear all notifications from local state
    setNotifications([]);
    setShowDeleteNotificationConfirm(false);
    setIsDeleteAll(false);
  };

  // Handler for opening cancel appointment modal
  const handleCancelAppointment = async () => {
    setIsCancelAppointmentButtonLoading(true);
    
    // Add a small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setShowCancelAppointmentModal(true);
    setShowAppointmentModal(false);
    
    setIsCancelAppointmentButtonLoading(false);
  };

  // Handler for going back to appointment details modal
  const handleGoBackToAppointment = async () => {
    setIsKeepAppointmentLoading(true);
    
    // Add a small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setShowCancelAppointmentModal(false);
    setShowAppointmentModal(true);
    setCancelReason("");
    setIsKeepAppointmentLoading(false);
  };

  // Handler for confirming appointment cancellation
  const closeCancelAppointmentModal = () => {
    setShowCancelAppointmentModal(false);
    setCancelReason("");
    clearCancelQueryParams();
  };

  const handleConfirmCancelAppointment = async () => {
    setIsCancelAppointmentLoading(true);
    
    try {
      // Add a small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Ensure we have an appointment ID. If missing, try to resolve from
      // URL params or by querying Firestore using date + startTime.
      let apptId = selectedAppointment?.id;
      let apptDate = selectedAppointment?.date;
      let apptStart = selectedAppointment?.startTime || selectedAppointment?.time;
      let apptDateKey = selectedAppointment?.dateKey || null;

      // Fallback to URL params if available
      if (!apptId) apptId = searchParams.get('appointmentId') || searchParams.get('apptId') || null;
      if (!apptDate) apptDate = searchParams.get('date') || searchParams.get('apptDate') || null;
      if (!apptDateKey) apptDateKey = searchParams.get('dateKey') || searchParams.get('dateISO') || null;
      if (!apptStart) apptStart = searchParams.get('startTime') || searchParams.get('apptStart') || null;

      if (!apptId && apptDate && apptStart && currentUserUid) {
        try {
          // Normalize date key to match Firestore document ids (YYYY-MM-DD)
          const normalizeDateKey = (d) => {
            if (!d) return null;
            if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
            const parsed = new Date(d);
            if (!isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0];
            return null;
          };

          const docKey = apptDateKey || normalizeDateKey(apptDate) || apptDate;
          const apptDocRef = doc(db, 'appointments', docKey);
          const apptDoc = await getDoc(apptDocRef);
          if (apptDoc.exists()) {
            const data = apptDoc.data();
            for (const [key, appt] of Object.entries(data)) {
              const matchStart = (appt.startTime || appt.time || '').toString() === apptStart.toString();
              const matchPatient = appt.patientId === currentUserUid;
              if (matchStart && matchPatient) {
                apptId = key;
                // update local selectedAppointment state with resolved id
                setSelectedAppointment((prev) => ({ ...(prev || {}), id: apptId }));
                break;
              }
            }
          }
        } catch (err) {
          console.error('Error resolving appointment id before cancel:', err);
        }
      }

      const cancelParams = cancelAppointmentParamsRef.current || {};
      if (!apptId) {
        apptId = cancelParams.apptId || null;
      }
      if (!apptDate) {
        apptDate = cancelParams.apptDate || null;
      }
      apptDateKey = apptDateKey || cancelParams.apptDateKey || null;
      if (!apptStart) {
        apptStart = cancelParams.apptStart || null;
      }

      if (!apptId) {
        console.error('No appointment ID found', { selectedAppointment, cancelParams });
        setIsCancelAppointmentLoading(false);
        return;
      }

      // Ensure we have the appointment date for the document path
      const appointmentDateRaw = apptDateKey || apptDate || selectedAppointment?.date || cancelParams.apptDate;
      if (!appointmentDateRaw) {
        console.error('No appointment date available for cancellation');
        setIsCancelAppointmentLoading(false);
        return;
      }

      // Normalize to YYYY-MM-DD doc id used in Firestore
      const normalizeDateKey = (d) => {
        if (!d) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
        const parsed = new Date(d);
        if (!isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0];
        return null;
      };

      const appointmentDocKey = normalizeDateKey(appointmentDateRaw) || appointmentDateRaw;

      // Delete the appointment from Firestore using the correct structure
      // Appointments are stored as: appointments/{dateKey YYYY-MM-DD} -> {appointmentId: appointmentData}
      const appointmentRef = doc(db, 'appointments', appointmentDocKey);

      // Resolve actual appointment id and document before deleting.
      let deleted = false;
      let finalDocRef = appointmentRef;
      let finalApptId = apptId;

      try {
        const apptDoc = await getDoc(appointmentRef);
        if (apptDoc.exists()) {
          const data = apptDoc.data() || {};
          // If provided apptId exists in this doc, use it. Otherwise try to find matching appointment by patient+startTime.
          if (finalApptId && Object.prototype.hasOwnProperty.call(data, finalApptId)) {
            // good
          } else {
            // attempt to find by matching startTime and patientId
            for (const [key, appt] of Object.entries(data)) {
              const matchStart = (appt.startTime || appt.time || '').toString() === (apptStart || '').toString();
              const matchPatient = appt.patientId === currentUserUid || appt.patientEmail === selectedAppointment?.patientEmail;
              if (matchStart && matchPatient) {
                finalApptId = key;
                break;
              }
            }
          }
          if (finalApptId && Object.prototype.hasOwnProperty.call(data, finalApptId)) {
            await updateDoc(appointmentRef, { [finalApptId]: deleteField() });
            deleted = true;
            console.log('Deleted appointment from computed doc', { docId: appointmentDocKey, finalApptId });
          }
        }
      } catch (err) {
        console.warn('Error checking computed appointment document', { err, appointmentDocKey, apptId });
      }

      // Fallback: scan all appointment docs to find the one containing this appointment or matching details
      if (!deleted) {
        try {
          const apptsCollection = collection(db, 'appointments');
          const apptsSnapshot = await getDocs(apptsCollection);
          let foundDoc = null;
          let foundKey = null;

          apptsSnapshot.forEach((d) => {
            const data = d.data() || {};
            // direct id match
            if (!foundDoc && finalApptId && Object.prototype.hasOwnProperty.call(data, finalApptId)) {
              foundDoc = d;
              foundKey = finalApptId;
              return;
            }
            // try matching by patient+startTime
            if (!foundDoc) {
              for (const [key, appt] of Object.entries(data)) {
                const matchStart = (appt.startTime || appt.time || '').toString() === (apptStart || '').toString();
                const matchPatient = appt.patientId === currentUserUid || appt.patientEmail === selectedAppointment?.patientEmail;
                if (matchStart && matchPatient) {
                  foundDoc = d;
                  foundKey = key;
                  break;
                }
              }
            }
          });

          if (foundDoc && foundKey) {
            await updateDoc(foundDoc.ref, { [foundKey]: deleteField() });
            deleted = true;
            console.log('Deleted appointment via fallback doc', { docId: foundDoc.id, apptId: foundKey });
            finalDocRef = foundDoc.ref;
            finalApptId = foundKey;
          } else {
            console.error('Fallback search did not find any appointment document containing appointment id or matching details', { finalApptId, apptStart });
          }
        } catch (err2) {
          console.error('Error during fallback search/delete for appointment cancellation', err2);
        }
      }

      if (!deleted) {
        throw new Error('Failed to delete appointment; document not found');
      }
      
      
      // --- Send notifications to clinic personnel and patient ---
      const usersRef = collection(db, "users");
      const usersSnapshot = await getDocs(usersRef);
      
      // Format appointment details for notifications
      const formattedDateLong = formatDateLong(selectedAppointment.date);
      const startTime = selectedAppointment.startTime || selectedAppointment.time;
      const endTime = selectedAppointment.endTime;
      const patientName = selectedAppointment.patientName || "Unknown Patient";
      
      // Format times consistently
      const formattedStartTime = formatTimeConsistent(startTime);
      const formattedEndTime = endTime ? formatTimeConsistent(endTime) : null;
      const appointmentWindow = `${formattedDateLong} (${formattedStartTime}${formattedEndTime ? ` - ${formattedEndTime}` : ""})`;
      
      const reasonValue = cancelReason && cancelReason.trim() ? cancelReason.trim() : "N/A";
      
      // Notify assigned dentist and all ClinicStaff
      const clinicNotificationMessage = `The appointment for ${patientName} on ${appointmentWindow} has been cancelled. Reason: ${reasonValue}.`;
      const assignedDentistId = selectedAppointment.dentistId;
      
      // Helper function to send email cancellation notification
      const sendEmailCancellationNotification = async (type, recipientEmail, recipientName = null, recipientRole = null) => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          // Format services - extract names if they're objects
          const servicesRaw = selectedAppointment.services || [];
          const services = Array.isArray(servicesRaw) 
            ? servicesRaw.map(service => {
                if (typeof service === 'string') {
                  return service;
                } else if (service && typeof service === 'object') {
                  return service.name || service.serviceName || JSON.stringify(service);
                }
                return String(service);
              })
            : [];
          const response = await fetch(`${apiUrl}/api/send-appointment-cancellation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type,
              recipientEmail,
              recipientName: recipientName || (type === 'patient' ? patientName : 'Doctor'),
              recipientRole: recipientRole || (type === 'patient' ? null : 'ClinicStaff'),
              patientName,
              appointmentDate: formattedDateLong,
              startTime: formattedStartTime,
              endTime: formattedEndTime || formattedStartTime,
              dentistName: selectedAppointment.dentist || 'N/A',
              services,
              reason: reasonValue,
              actorName: patientName, // Patient is cancelling their own appointment
              clinicName: 'VSDiamond Dental Clinic'
            })
          });
          const result = await response.json();
          if (!result.success) {
            console.error(`Failed to send cancellation email notification to ${type}:`, result.message);
          }
          return result;
        } catch (error) {
          console.error(`Error sending cancellation email notification to ${type}:`, error);
          return { success: false, error };
        }
      };
      
      const emailPromises = [];
      for (const docu of usersSnapshot.docs) {
        const data = docu.data();
        const userId = docu.id;
        const userRole = data.userType || data.role;
        
        // Notify the assigned dentist (DentistOwner or AssociateDentist) if their UID matches
        if (
          (userRole === "DentistOwner" || userRole === "AssociateDentist") &&
          assignedDentistId &&
          userId === assignedDentistId
        ) {
          addNotification(userId, clinicNotificationMessage);
          
          // Send email notification if dentist's email is verified
          const dentistEmailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
          const dentistEmail = data.email || data.authEmail;
          if (dentistEmailVerified && dentistEmail && !dentistEmail.includes("@mobileuser.dentalapp.com")) {
            const dentistName = data.firstName && data.lastName 
              ? `${data.firstName} ${data.lastName}` 
              : (data.name || "Doctor");
            console.log('Sending dentist cancellation email to:', dentistEmail);
            emailPromises.push(sendEmailCancellationNotification('dentist', dentistEmail, dentistName, userRole));
          } else {
            console.warn('Skipping dentist email:', { dentistEmailVerified, dentistEmail, isDentist: !dentistEmail.includes("@mobileuser.dentalapp.com") });
          }
        } else if (userRole === "ClinicStaff") {
          addNotification(userId, clinicNotificationMessage);
          
          // Send email notification if staff's email is verified
          const staffEmailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
          const staffEmail = data.email || data.authEmail;
          if (staffEmailVerified && staffEmail && !staffEmail.includes("@mobileuser.dentalapp.com")) {
            const staffName = data.firstName && data.lastName 
              ? `${data.firstName} ${data.lastName}` 
              : (data.name || "Staff");
            console.log('Sending staff cancellation email to:', staffEmail);
            emailPromises.push(sendEmailCancellationNotification('dentist', staffEmail, staffName, userRole));
          }
        }
      }
      
      // Notify the patient
      if (selectedAppointment.patientId) {
        const patientNotificationMessage = `Your appointment on ${appointmentWindow} has been cancelled. Reason: ${reasonValue}.`;
        addNotification(selectedAppointment.patientId, patientNotificationMessage);
        
        // Send email notification to patient if their email is verified
        // Get patient data to check email verification status
        const patientDoc = usersSnapshot.docs.find(doc => doc.id === selectedAppointment.patientId);
        if (patientDoc) {
          const patientData = patientDoc.data();
          const patientEmailVerified = patientData.emailVerified === "Yes" || patientData.emailVerified === true;
          const patientEmail = patientData.email || patientData.authEmail;
          if (patientEmailVerified && patientEmail && !patientEmail.includes("@mobileuser.dentalapp.com")) {
            console.log('Sending patient cancellation email to:', patientEmail);
            emailPromises.push(sendEmailCancellationNotification('patient', patientEmail, patientName));
          } else {
            console.warn('Skipping patient email:', { patientEmailVerified, patientEmail, patientId: selectedAppointment.patientId });
          }
        } else {
          console.warn('Patient document not found in usersSnapshot:', selectedAppointment.patientId);
        }
      } else {
        console.warn('No patientId in selectedAppointment');
      }
      
      if (emailPromises.length > 0) {
        await Promise.all(emailPromises);
      }
      // ---------------------------------------------------------------
      
      // Update the UI by removing the appointment from the local state
      setLatestPatientAppointments(prev => 
        prev.filter(appointment => appointment.id !== selectedAppointment.id)
      );
      
      // Also update latestAppointments if it exists there
      setLatestAppointments(prev => 
        prev.filter(appointment => appointment.id !== selectedAppointment.id)
      );
      
      // Close modals and reset state
      closeCancelAppointmentModal();
      setShowAppointmentModal(false);
      setSelectedAppointment(null);
      
      // Show success message
      setMessage("Appointment cancelled successfully");
      setSnack(true);
      
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      
      // Show error message
      setMessage("Failed to cancel appointment. Please try again.");
      setSnack(true);
      
    } finally {
      setIsCancelAppointmentLoading(false);
    }
  };



  // Helper to format time as AM/PM
  function formatTime12hr(timeStr) {
    if (!timeStr) return "";
    // If already contains AM/PM, just return trimmed
    if (/am|pm/i.test(timeStr)) return timeStr.trim();
    // Handles "HH:mm" or "HH:mm:ss"
    const [hour, minute] = timeStr.split(":");
    let h = parseInt(hour, 10);
    const m = minute ? minute.padStart(2, "0") : "00";
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  }

  // Compute end time string by adding minutes to a start time string
  function addMinutesToTimeStr(startStr, minutesToAdd) {
    if (!startStr) return "";
    const s = String(startStr).trim();
    // Parse "H:MM AM/PM" or "HH:mm"
    let hours = 0;
    let minutes = 0;
    const ampm = /(\d{1,2}):?(\d{0,2})\s*(AM|PM)/i.exec(s);
    if (ampm) {
      hours = parseInt(ampm[1] || "0", 10);
      minutes = parseInt(ampm[2] || "0", 10);
      const isPM = /PM/i.test(ampm[3]);
      if (isPM && hours < 12) hours += 12;
      if (!isPM && hours === 12) hours = 0;
    } else {
      const hhmm = /(\d{1,2}):(\d{2})/.exec(s);
      if (hhmm) {
        hours = parseInt(hhmm[1] || "0", 10);
        minutes = parseInt(hhmm[2] || "0", 10);
      } else {
        const numeric = /^\d{1,4}$/.exec(s.replace(/:/g, ""));
        if (numeric) {
          const n = numeric[0];
          if (n.length <= 2) {
            hours = parseInt(n, 10) || 0;
            minutes = 0;
          } else {
            hours = parseInt(n.slice(0, n.length - 2), 10) || 0;
            minutes = parseInt(n.slice(-2), 10) || 0;
          }
        }
      }
    }
    const base = new Date(1970, 0, 1, hours, minutes, 0, 0);
    const add = Number(minutesToAdd) || 0;
    base.setMinutes(base.getMinutes() + add);
    return base.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }

  function formatDateMMDDYYYY(dateInput) {
  if (!dateInput) return "";
  // If already a Date object
  if (dateInput instanceof Date && !isNaN(dateInput)) {
    const mm = String(dateInput.getMonth() + 1).padStart(2, "0");
    const dd = String(dateInput.getDate()).padStart(2, "0");
    const yyyy = dateInput.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  }
  // If string "YYYY-MM-DD"
  if (typeof dateInput === "string") {
    const s = dateInput.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [year, month, day] = s.split("-");
      return `${month}-${day}-${year}`;
    }
    
    const parsed = new Date(s);
    if (!isNaN(parsed)) {
      const mm = String(parsed.getMonth() + 1).padStart(2, "0");
      const dd = String(parsed.getDate()).padStart(2, "0");
      const yyyy = parsed.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    }
  }
  return "";
}

  // In fetchAppointments, use each widget's days range for its own data
  useEffect(() => {
    // Only fetch data if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    const appointmentsRef = collection(db, "appointments");
    
    // Use onSnapshot for real-time updates
    const unsubscribe = onSnapshot(
      appointmentsRef,
      (snapshot) => {
        try {
          let allAppointments = [];
          let patientAppointmentCounts = {};
          let serviceCounts = {};

          snapshot.forEach((dateDoc) => {
            const dateKey = dateDoc.id;
            const appointmentsObj = dateDoc.data();
            for (const apptId in appointmentsObj) {
              const appt = appointmentsObj[apptId];
              appt._dateKey = dateKey;
              appt._id = apptId;
              allAppointments.push(appt);

              if (appt.patientId) {
                patientAppointmentCounts[appt.patientId] =
                  (patientAppointmentCounts[appt.patientId] || 0) + 1;
              }

              if (Array.isArray(appt.services)) {
                appt.services.forEach((service) => {
                  let serviceName =
                    typeof service === "object" && service.name
                      ? service.name
                      : service;
                  if (serviceName) {
                    serviceCounts[serviceName] =
                      (serviceCounts[serviceName] || 0) + 1;
                  }
                });
              } else if (typeof appt.services === "string") {
                appt.services
                  .split(",")
                  .map((s) => s.trim())
                  .forEach((service) => {
                    if (service)
                      serviceCounts[service] = (serviceCounts[service] || 0) + 1;
                  });
              }
            }
          });

          // Helper to get appointments in range
          const getAppointmentsInRange = (rangeType) => {
            const now = new Date();
            now.setHours(0, 0, 0, 0);

            if (rangeType === "7") {
              const daysAgo = new Date(now);
              daysAgo.setDate(now.getDate() - 6);
              daysAgo.setHours(0, 0, 0, 0);
              return allAppointments.filter((appt) => {
                const apptDate = new Date(appt._dateKey);
                apptDate.setHours(0, 0, 0, 0);
                return apptDate >= daysAgo && apptDate <= now;
              });
            }
            if (rangeType === "30") {
              const daysAgo = new Date(now);
              daysAgo.setDate(now.getDate() - 29);
              daysAgo.setHours(0, 0, 0, 0);
              return allAppointments.filter((appt) => {
                const apptDate = new Date(appt._dateKey);
                apptDate.setHours(0, 0, 0, 0);
                return apptDate >= daysAgo && apptDate <= now;
              });
            }
            if (rangeType === "365") {
              const yearAgo = new Date(now);
              yearAgo.setFullYear(now.getFullYear() - 1);
              yearAgo.setHours(0, 0, 0, 0);
              return allAppointments.filter((appt) => {
                const apptDate = new Date(appt._dateKey);
                apptDate.setHours(0, 0, 0, 0);
                return apptDate >= yearAgo && apptDate <= now;
              });
            }
            if (rangeType === "all") {
              return allAppointments;
            }
            return [];
          };
          
          // Total Appointments
          setTotalAppointments(
            getAppointmentsInRange(appointmentsRangeType).length
          );

          // Total Income
          const getAppointmentRevenue = (appt) => {
            if (appt.totalBill !== undefined) return Number(appt.totalBill) || 0;
            if (appt.bill !== undefined) return Number(appt.bill) || 0;
            return Number(appt.totalFee) || 0;
          };
          const income = getAppointmentsInRange(incomeRangeType).reduce(
            (sum, appt) => sum + getAppointmentRevenue(appt),
            0
          );
          setTotalIncome(income);

          // New vs Returning Patients
          let newCount = 0,
            returningCount = 0;
          const patientCounts = {};
          getAppointmentsInRange(patientsRangeType).forEach((appt) => {
            if (appt.patientId) {
              patientCounts[appt.patientId] =
                (patientCounts[appt.patientId] || 0) + 1;
            }
          });
          Object.values(patientCounts).forEach((count) => {
            if (count === 1) newCount++;
            else if (count > 1) returningCount++;
          });
          setNewPatients(newCount);
          setReturningPatients(returningCount);

          // Latest appointments (last 3, sorted by date and time)
          const extractStartTime = (a) => {
            if (!a) return "00:00";
            if (a.startTime) return String(a.startTime).trim();
            if (a.time) {
              const t = String(a.time);
              return t.includes("-") ? t.split("-")[0].trim() : t.trim();
            }
            return "00:00";
          };

          const timeStringToMinutes = (s) => {
            if (!s) return 0;
            const str = String(s).trim();
            const ampm = /(\d{1,2}):?(\d{0,2})\s*(AM|PM)/i.exec(str);
            if (ampm) {
              let hh = parseInt(ampm[1], 10) || 0;
              const mm = parseInt(ampm[2] || "0", 10) || 0;
              const isPM = /PM/i.test(ampm[3]);
              if (isPM && hh < 12) hh += 12;
              if (!isPM && hh === 12) hh = 0;
              return hh * 60 + mm;
            }
            const twentyFour = /(\d{1,2}):(\d{2})/.exec(str);
            if (twentyFour) {
              const hh = parseInt(twentyFour[1], 10) || 0;
              const mm = parseInt(twentyFour[2], 10) || 0;
              return hh * 60 + mm;
            }
            return 0;
          };

          const appointmentToDate = (appt) => {
            if (!appt || !appt._dateKey) return new Date(0);
            const start = extractStartTime(appt) || "00:00";
            const [hh, mm] = (() => {
              const mins = timeStringToMinutes(start);
              return [Math.floor(mins / 60), mins % 60];
            })();
            const [y, m, d] = appt._dateKey.split("-").map((v) => Number(v));
            const dt = new Date(y, (m || 1) - 1, d || 1);
            dt.setHours(hh || 0, mm || 0, 0, 0);
            return dt;
          };

          const now = new Date();
          now.setHours(0, 0, 0, 0);
          let rangeStart = new Date(now); // Start from current date
          let rangeEnd = null;
          
          if (latestRangeType === "7") {
            rangeEnd = new Date(now);
            rangeEnd.setDate(now.getDate() + 6);
          } else if (latestRangeType === "30") {
            rangeEnd = new Date(now);
            rangeEnd.setDate(now.getDate() + 29);
          } else if (latestRangeType === "365") {
            rangeEnd = new Date(now);
            rangeEnd.setFullYear(now.getFullYear() + 1);
          } else if (latestRangeType === "all") {
            rangeEnd = null; // No end date for "all"
          }
          
          // Only include appointments from current date onwards
          const inRange = allAppointments.filter((appt) => {
            if (!appt || !appt._dateKey) return false;
            const apptDate = new Date(appt._dateKey);
            apptDate.setHours(0, 0, 0, 0);
            
            // Must be >= current date
            if (apptDate < rangeStart) return false;
            
            // Check if within range end (if specified)
            if (rangeEnd && apptDate > rangeEnd) return false;
            
            return true;
          });

          const filtered = inRange.filter(
            (a) =>
              a &&
             ["pending", "confirmed"].includes(String(a.status || "").toLowerCase())
          );

          // Filter by dentist based on role and selected filter
          let filteredByDentist = filtered;
          if (userDetails && auth.currentUser) {
            const userRole = userDetails.role || userDetails.userType;
            const currentUserUid = auth.currentUser.uid;
            
            if (userRole === "AssociateDentist") {
              // Associate dentists only see their own appointments
              const firstName = (userDetails.firstName || "").trim();
              const lastName = (userDetails.lastName || "").trim();
              const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
              const dentistDisplayName = fullName ? `Dr. ${fullName}` : null;
              
              filteredByDentist = filtered.filter((appt) => {
                // Check if appointment belongs to current dentist by dentistId
                if (appt.dentistId && appt.dentistId === currentUserUid) {
                  return true;
                }
                // Fallback: check by dentist display name
                if (dentistDisplayName && appt.dentist === dentistDisplayName) {
                  return true;
                }
                return false;
              });
            } else if (userRole === "DentistOwner" || userRole === "ClinicStaff") {
              // DentistOwner and ClinicStaff can filter by specific dentist or see all
              if (selectedLatestDentist !== "all") {
                const selectedDentist = dentistsList.find(d => d.id === selectedLatestDentist);
                if (selectedDentist) {
                  const dentistDisplayName = selectedDentist.name;
                  filteredByDentist = filtered.filter((appt) => {
                    // Check if appointment belongs to selected dentist by dentistId
                    if (appt.dentistId && appt.dentistId === selectedLatestDentist) {
                      return true;
                    }
                    // Fallback: check by dentist display name
                    if (appt.dentist === dentistDisplayName) {
                      return true;
                    }
                    return false;
                  });
                }
              }
              // If "all" is selected, show all appointments (no filtering)
            }
          }

          const sortedLatest = [...filteredByDentist]
            .map((a) => ({ raw: a, dateTime: appointmentToDate(a) }))
            .sort((x, y) => x.dateTime - y.dateTime) // oldest first
            .slice(0, 3)
            .map(({ raw }) => {
              const start = extractStartTime(raw);
              let timeDisplay = "";
              if (raw.startTime && raw.endTime) {
                timeDisplay = `${formatTime12hr(raw.startTime)} - ${formatTime12hr(
                  raw.endTime
               )}`;
              } else if (start) {
                timeDisplay = formatTime12hr(start);
              }
              return {
                name: raw.patientName || raw.patientEmail || "Unknown",
                service: Array.isArray(raw.services)
                  ? raw.services
                      .map((s) => (typeof s === "object" && s.name ? s.name : s))
                      .join(", ")
                  : raw.services,
                time: timeDisplay,
                status: raw.status || "Pending",
                _dateKey: raw._dateKey,
                _id: raw._id || raw.id,
                // add display date in MM-DD-YYYY for UI
                displayDate: formatDateMMDDYYYY(raw._dateKey || raw.date || ""),
              };
            });
          setLatestAppointments(sortedLatest);

          // Popular services (top 3 in range)
          const filteredServiceCounts = {};
          getAppointmentsInRange(popularRangeType).forEach((appt) => {
            if (Array.isArray(appt.services)) {
              appt.services.forEach((service) => {
                let serviceName =
                  typeof service === "object" && service.name
                    ? service.name
                    : service;
                if (serviceName) {
                  filteredServiceCounts[serviceName] =
                    (filteredServiceCounts[serviceName] || 0) + 1;
                }
              });
            } else if (typeof appt.services === "string") {
              appt.services
                .split(",")
                .map((s) => s.trim())
                .forEach((service) => {
                  if (service)
                    filteredServiceCounts[service] =
                      (filteredServiceCounts[service] || 0) + 1;
                });
            }
          });
          const popular = Object.entries(filteredServiceCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([name, count]) => ({ name, count }));
          setPopularServices(popular);
        } catch (error) {
          console.error("Error fetching appointments:", error);
          // Don't show error to user if it's a permission error during redirect
          if (error.code !== "permission-denied") {
            console.error("Failed to fetch appointments data");
          }
        }
      },
      (error) => {
        console.error("Error in appointments snapshot:", error);
        if (error.code !== "permission-denied") {
          console.error("Failed to listen to appointments data");
        }
      }
    );

    // Return unsubscribe function to clean up listener
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointmentsRangeType,
    incomeRangeType,
    patientsRangeType,
    latestRangeType,
    popularRangeType,
    isAuthenticated,
    userDetails,
    selectedLatestDentist,
    dentistsList,
  ]);

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    const fetchAppointments = async () => {
      try {
        const appointmentsRef = collection(db, "appointments");
        const dateDocs = await getDocs(appointmentsRef);

        let allAppointments = [];
        let patientAppointmentCounts = {};

        for (const dateDoc of dateDocs.docs) {
          const dateKey = dateDoc.id;
          const appointmentsObj = dateDoc.data();
          for (const apptId in appointmentsObj) {
            const appt = appointmentsObj[apptId];
            appt._dateKey = dateKey;
            appt._id = apptId;
            allAppointments.push(appt);

            if (appt.patientId) {
              patientAppointmentCounts[appt.patientId] =
                (patientAppointmentCounts[appt.patientId] || 0) + 1;
            }
          }
        }

        // Helper to get appointments in range
        const getAppointmentsInRange = (rangeType) => {
          const now = new Date();
          now.setHours(0, 0, 0, 0);

          if (rangeType === "7") {
            const daysAgo = new Date(now);
            daysAgo.setDate(now.getDate() - 6);
            daysAgo.setHours(0, 0, 0, 0);
            return allAppointments.filter((appt) => {
              const apptDate = new Date(appt._dateKey);
              apptDate.setHours(0, 0, 0, 0);
              return apptDate >= daysAgo && apptDate <= now;
            });
          }
          if (rangeType === "30") {
            const daysAgo = new Date(now);
            daysAgo.setDate(now.getDate() - 29);
            daysAgo.setHours(0, 0, 0, 0);
            return allAppointments.filter((appt) => {
              const apptDate = new Date(appt._dateKey);
              apptDate.setHours(0, 0, 0, 0);
              return apptDate >= daysAgo && apptDate <= now;
            });
          }
          if (rangeType === "365") {
            const yearAgo = new Date(now);
            yearAgo.setFullYear(now.getFullYear() - 1);
            yearAgo.setHours(0, 0, 0, 0);
            return allAppointments.filter((appt) => {
              const apptDate = new Date(appt._dateKey);
              apptDate.setHours(0, 0, 0, 0);
              return apptDate >= yearAgo && apptDate <= now;
            });
          }
          if (rangeType === "all") {
            return allAppointments;
          }
          return [];
        };


        // Popular Services (top 3)
        const serviceCounts = {};
        allAppointments.forEach((appt) => {
          if (Array.isArray(appt.services)) {
            appt.services.forEach((service) => {
              let serviceName =
                typeof service === "object" && service.name
                  ? service.name
                  : service;
              if (serviceName) {
                serviceCounts[serviceName] =
                  (serviceCounts[serviceName] || 0) + 1;
              }
            });
          } else if (typeof appt.services === "string") {
            appt.services
              .split(",")
              .map((s) => s.trim())
              .forEach((service) => {
                if (service)
                  serviceCounts[service] = (serviceCounts[service] || 0) + 1;
              });
          }
        });
        const popular = Object.entries(serviceCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([name, count]) => ({ name, count }));
        setPopularServices(popular);

        // Total Appointments
        setTotalAppointments(
          getAppointmentsInRange(appointmentsRangeType).length
        );

        // Total Pending Appointments
        setTotalPendingAppointments(
          getAppointmentsInRange(pendingRangeType).filter(
            (appt) => appt.status === "Pending" || appt.status === "New"
          ).length
        );

        // New vs Returning Patients
        let newCount = 0,
          returningCount = 0;
        const patientCounts = {};
        getAppointmentsInRange(patientsRangeType).forEach((appt) => {
          if (appt.patientId) {
            patientCounts[appt.patientId] =
              (patientCounts[appt.patientId] || 0) + 1;
          }
        });
        Object.values(patientCounts).forEach((count) => {
          if (count === 1) newCount++;
          else if (count > 1) returningCount++;
        });
        setNewPatients(newCount);
        setReturningPatients(returningCount);
      } catch (error) {
        console.error("Error fetching appointments:", error);
        // Don't show error to user if it's a permission error during redirect
        if (error.code !== "permission-denied") {
          console.error("Failed to fetch appointments data");
        }
      }
    };

    fetchAppointments();
  }, [
    appointmentsRangeType,
    pendingRangeType,
    patientsRangeType,
    isAuthenticated,
  ]);

  // UseEffects
  useEffect(() => {
    // const details = fetchUserDetails(setUserDetails);
    const redirect = redirectIfNotLoggedIn(setShowCustomAlert, navigate);
    
    // Force refresh user data when component mounts to sync any auth changes
    if (user) {
      fetchUserDetails(setUserDetails, user.uid);
    }
    
    // const notify = notifyFirstUser();
    return () => {
      // details && details();
      redirect && redirect();
      // notify && notify();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Close delete confirmation modal

  const boxStylesContent = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    overflow: "hidden",
    backgroundColor: "#FDF9F3",
    // width: "100%"
  };




  // Styling for the text field label
  const styleText = {
    color: "#2C251E",
    fontWeight: "600",
    fontSize: "14px",
    padding: "5px 0",
    alignContent: "center",
  };

  // Styling for the text field label
  const styleTextSelect = {
    color: "#2C251E",
    fontWeight: "100",
    fontSize: "14px",
    padding: "5px 0",
  };

  // Insurance companies list
  const insuranceCompanies = [
    "Intellicare (Your Intelligent Health Choice)",
    "MediCard",
    "MaxiCare (Live Your Best Life)",
    "AVEGA",
    "flexicare",
    "InLife (Insulare Health Care)",
    "HMI (Health Maintenance, Inc.)",
    "Lacson & Lacson (Insurance Brokers, Inc.)",
    "ValuCare (Healthcare You Can Count On)",
    "PhilCare (Healthcare Made Smarter)",
    "ASIANLIFE & GENERAL ASSURANCE CORPORATION",
    "EastWest Healthcare (Securing a Healthy Horizon)",
    "COCOLIFE HEALTHCARE",
    "KAISER INTERNATIONAL (Health Group, Inc.)",
    "eTiQa Insurance",
    "Life & Health (HMP Incorporated)",
    "DNC (Dental Network Company)",
    "Health Partners Dental Access, Inc.",
    "Getwell Health Systems",
    "GENERAL"
  ];

  const styleInsuranceLabel = {
    color: "#2C251E",
    fontWeight: "300",
    fontSize: "14px",
    padding: "5px 0",
    opacity: "0.6",
  };

  const styleInsuranceText = {
    color: "#2C251E",
    fontWeight: "300",
    fontSize: "14px",
    padding: "5px 0",
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return <SplashScreen isOpen={true} />;
  }

  // Show nothing if not authenticated (redirect will happen)
  if (!isAuthenticated) {
    return null;
  }

  // Skip email verification check - ProtectedRoute already handles this

  return (
    <div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* All Content */}
      <Box sx={boxStylesContent}>
        {/* Sidebar */}
        <Box
          display={{
            xs: "none",
            sm: "none",
            md: "block",
            lg: "block",
            xl: "block",
          }}>
        <Sidebar
          fName={userDetails?.firstName}
          lName={userDetails?.lastName}
          email={userDetails?.email}
        />
        </Box>

        <Box
          sx={{
            display: {
              xs: "block",
              sm: "block",
              md: "none",
              lg: "none",
              xl: "none",
            }
          }}
        >
        <Fab
          label={userDetails?.role === "Patient" ? "Schedule Appointment" : "Manage Appointment"}
          place="top-end"
          destination={
            userDetails?.role === "Patient" ? "/patient-appointment-booking" : "/manage-appointment"}
          src={require("../../assets/icons/new_appointment.png")}
          open={false}/>
        </Box>

        {/*Grids */}
        <Grid
          container
          // rowSpacing={2}
          // columnSpacing={2}
          sx={{
            width: "100%",
            height: "100vh",
            // backgroundColor: "blue",
            "& .MuiGrid-item": {
              display: "flex",
              justifyContent: "center",
              // backgroundColor: "blue",
            },
          }}
        >
          {/* Main Content */}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
          >
            {/* Main Content */}
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "#FDF9F3",
                // backgroundColor: "#f8f5ef",
              }}
            >
              <Stack direction="column">
                {/* Header Container */}
                <Header
                  count={3}
                  page="Dashboard"
                  setShowNotifications={setShowNotifications}
                  unreadCount={unreadCount}
                  showNotifications={showNotifications}
                  notifications={notifications}
                  setNotifications={setNotifications}
                  setShowNotificationsModal={setShowNotificationsModal}
                />

                {/*Grids */}
                <Grid
                  container
                  sx={{
                    width: "100%",
                    height: "calc(100vh - 96px)",
                    overflowY: "auto",
                    // backgroundColor: "blue",
                    "& .MuiGrid-item": {
                      display: "flex",
                      justifyContent: "center",
                      // backgroundColor: "blue",
                    },
                  }}
                >

                {/* Widgets Container*/}
                <Box
                  sx={{
                    gap: {
                      xs: "8px",
                      sm: "8px",
                      md: "8px",
                      lg: "8px",
                      xl: "0px",
                    },
                    display: "flex",
                    // display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    width: "100%",
                    mt: "0px",
                    // padding: "0px"
                  }}
                >
                  {/* Top Widgets */}
                  {userDetails?.role !== "ClinicStaff" && (<Grid
                    container
                    spacing={3}
                    sx={{
                      width: "100%",
                      padding: "20px",
                      paddingBottom: "10px",
                      // backgroundColor: "blue",
                      "& .MuiGrid-item": {
                        display: "flex",
                        justifyContent: "center",
                        // backgroundColor: "blue",
                      },
                    }}
                  >
                    {/* Next Appointment && Total Appointments*/}
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }} item>
                      <Box
                        p={2}
                        border="1px solid #0000010ff"
                        backgroundColor="#fefefe"
                        sx={{
                          borderRadius: "16px",
                          boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
                          // border: "1px solid rgba(89, 84, 74, .20)",
                        }}
                      >
                        <CardContent
                          sx={{
                            padding: "20px"
                            
                          }}
                        >
                          {userDetails?.role === "ClinicStaff" ? (
                            <>
                              <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                  <Box sx={{ width: "40px", height: "40px", borderRadius: "8px", backgroundColor: "#2E2D2D", boxShadow: "0 1px 5px rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Icon src={activeStatsWidget === "appointments" ? appointments : activeStatsWidget === "pending" ? pending : patients} size="small" />
                                  </Box>
                                  <Selected
                                    value={activeStatsWidget}
                                    onChange={(e) => setActiveStatsWidget(e.target.value)}
                                    variant="standard"
                                    disableUnderline
                                    sx={{ "& .MuiSelect-select": { padding: "0 24px 0 0", fontWeight: 700, fontSize: "16px", color: "#2C251E" }, "& .MuiSvgIcon-root": { color: "#C5A572" }, "&:before": { display: "none" }, "&:after": { display: "none" } }}
                                    MenuProps={{ PaperProps: { sx: { backgroundColor: "#FEFEFE", color: "#2C251E", borderRadius: "10px", marginTop: "10px", border: "1.5px solid rgba(197,165,114,0.6)", "& .MuiMenuItem-root": { fontSize: "14px", borderRadius: "6px", "&:hover": { backgroundColor: "rgba(197,165,114,0.2)" } }, "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: "rgba(44,37,30,0.9) !important", color: "#FEFEFE !important" } } } }}
                                  >
                                    <MenuItem value="appointments">Total Appointments</MenuItem>
                                    <MenuItem value="pending">Pending Appointments</MenuItem>
                                    <MenuItem value="patients">Total Patients</MenuItem>
                                  </Selected>
                                </Stack>
                                <FormControl sx={{ maxWidth: "100px", minWidth: "100px" }}>
                                  <Selected
                                    value={activeStatsWidget === "appointments" ? appointmentsRangeType : activeStatsWidget === "pending" ? pendingRangeType : patientsRangeType}
                                    onChange={(e) => {
                                      if (activeStatsWidget === "appointments") setAppointmentsRangeType(e.target.value);
                                      else if (activeStatsWidget === "pending") setPendingRangeType(e.target.value);
                                      else setPatientsRangeType(e.target.value);
                                    }}
                                    sx={{ maxWidth: "150px", fontSize: "14px", "& .MuiSelect-select": { padding: "10px 15px", color: "#2C251E" }, "& .MuiOutlinedInput-notchedOutline": { border: "1.5px solid rgba(197,165,114,0.6)", borderRadius: "8px" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(197,165,114,0.9)" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(197,165,114,1)" }, "& .MuiSvgIcon-root": { color: "#2C251E" } }}
                                    MenuProps={{ PaperProps: { sx: { backgroundColor: "#FEFEFE", color: "#2C251E", borderRadius: "10px", marginTop: "10px", border: "1.5px solid rgba(197,165,114,0.6)", "& .MuiMenuItem-root": { fontSize: "14px", borderRadius: "6px", "&:hover": { backgroundColor: "rgba(197,165,114,0.2)" } }, "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: "rgba(44,37,30,0.9) !important", color: "#FEFEFE !important" } } } }}
                                  >
                                    <MenuItem value="7">7 days</MenuItem>
                                    <MenuItem value="30">30 days</MenuItem>
                                    <MenuItem value="365">1 year</MenuItem>
                                    <MenuItem value="all">All time</MenuItem>
                                  </Selected>
                                </FormControl>
                              </Stack>
                              {activeStatsWidget === "patients" ? (
                                <Stack direction="row" alignItems="center" spacing={3} sx={{ padding: "15px 10px" }}>
                                  <Box>
                                    <Typography sx={{ fontSize: "12px", color: "#888", fontWeight: 500 }}>New</Typography>
                                    <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700, color: "#2C251E" }}>
                                      {userDataLoading ? <CircularProgress size={24} sx={{ color: "#C5A572" }} /> : (newPatients || 0)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ width: "1px", height: 40, backgroundColor: "rgba(197,165,114,0.5)" }} />
                                  <Box>
                                    <Typography sx={{ fontSize: "12px", color: "#888", fontWeight: 500 }}>Returning</Typography>
                                    <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700, color: "#C5A572" }}>
                                      {userDataLoading ? <CircularProgress size={24} sx={{ color: "#C5A572" }} /> : (returningPatients || 0)}
                                    </Typography>
                                  </Box>
                                </Stack>
                              ) : (
                                <Typography variant="h2" sx={{ padding: "15px 10px", fontSize: "24px" }}>
                                  {userDataLoading ? (
                                    <CircularProgress size={24} sx={{ color: "#C5A572" }} />
                                  ) : activeStatsWidget === "appointments" ? (
                                    totalAppointments || "Not Available"
                                  ) : (
                                    totalPendingAppointments || "0"
                                  )}
                                </Typography>
                              )}
                            </>
                          ) : (
                          <>
                          <Stack
                            direction="row"
                            sx={{
                              // backgroundColor: "aqua",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            {userDetails?.role !== "Patient" || nextAppointment ? (
                            <Stack
                              direction="row"
                              sx={{
                                alignContent: "center",
                                alignItems: "center",
                                // backgroundColor: "mediumaquamarine",
                                textAlign: "center",
                                justifyItems: "center",
                              }}
                            >
<Box
                sx={{
                  width: "40px",
                  height: "40px",
                  // border: "1px solid black",
                  borderRadius: "8px",
                  backgroundColor: "#2E2D2D",
                  boxShadow: '0 1px 5px rgba(0,0,0,0.3)',
                }}>
                              <Icon
                                src={
                                  userDetails?.role === "Patient"
                                    ? calendar
                                    : appointments
                                }
                                size="small"
                              />
                              </Box>

                              <Text
                                text={
                                  userDetails?.role === "Patient"
                                    ? "Next Appointment"
                                    : "Total Appointments"
                                }
                                variant="h3Header"
                              />
                            </Stack>
                            ) : (
                              <Stack
                              direction="row"
                              sx={{
                                width: "100%",
                              justifyContent: "space-between",
                                alignContent: "center",
                                alignItems: "center",
                                // backgroundColor: "mediumaquamarine",
                                textAlign: "center",
                                justifyItems: "center",
                              }}
                            >

                              <Text
                                text={
                                  userDetails?.role === "Patient"
                                    ? "Next Appointment"
                                    : "Total Appointments"
                                }
                                variant="h3Header"
                              />
                              <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  // border: "1px solid black",
                  borderRadius: "8px",
                  backgroundColor: "#2E2D2D",
                  boxShadow: '0 1px 5px rgba(0,0,0,0.3)',
                }}>
                              <Icon
                                src={
                                  userDetails?.role === "Patient"
                                    ? calendar
                                    : appointments
                                }
                                size="small"
                              />
</Box>
                            </Stack>
                            )}

                            {nextAppointment && (
                              <div
                                className="dropdown-container"
                                style={{
                                  // position: "absolute",
                                  // background: "rgba(0, 0, 0, 0.1)",
                                  right: "0",
                                }}
                              >
                                <Box>
      <Tooltip
        title="View Appointment Details"
        placement="bottom"
        arrow
      >
                                  <Button
                                    disableRipple
                                    onClick={() =>
                                            handleAppointmentAction(
                                              nextAppointment,
                                              "view"
                                            )
                                          }
                                    sx={{
                                      // background: "rgba(0, 0, 0, 0.1)",
                                      cursor: "pointer",
                                      fontSize: "18px",
                                      color: "black",
                                      padding: "2px",
                                      ":hover": {
                                        background: "rgba(0, 0, 0, 0.1)",
                                      },
                                    }}
                                  >
                                    ⋮
                                  </Button>
</Tooltip>

                                  {/* {dropdownOpen ===
                                    `next-${nextAppointment.id}` && (
                                    <div>
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          background: "white",
                                          border: "1px solid #ddd",
                                          borderRadius: "5px",
                                          boxShadow:
                                            "0 2px 5px rgba(0, 0, 0, 0.2)",
                                          zIndex: 1000,
                                          minWidth: {
                                            xs : "0",
                                            sm : "0",
                                            md : "120px",
                                            lg : "120px",
                                            xl : "120px",
                                          },
                                        }}
                                      >
                                        <Button
                                          onMouseEnter={(e) =>
                                            (e.target.style.background =
                                              "#f5f5f5")
                                          }
                                          onMouseLeave={(e) =>
                                            (e.target.style.background =
                                              "transparent")
                                          }
                                          onClick={() =>
                                            handleAppointmentAction(
                                              nextAppointment,
                                              "view"
                                            )
                                          }
                                          disableRipple
                                          sx={{
                                            width: "100%",
                                            padding: "8px 12px",
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            textAlign: "left",
                                            fontSize: "14px",
                                            color: "black",
                                            textTransform: "none",
                                          }}
                                        >
                                          View Details
                                        </Button>
                                      </Box>
                                    </div>
                                  )} */}
                                </Box>
                              </div>
                            )}

                            {userDetails?.role !== "Patient" && (
                              <FormControl
                                sx={{
                                  maxWidth: "100px",
                                  minWidth: "100px",
                                }}
                              >
                                <Selected
                                  labelId="simple-select-label"
                                  id="simple-select"
                                  value={appointmentsRangeType}
                                  onChange={(e) =>
                                    setAppointmentsRangeType(e.target.value)
                                  }
                                  sx={{
                                    maxWidth: "150px",
                                    fontSize: "14px",
                                    "& .MuiSelect-select": {
                                      padding: "10px 15px",
                                      color: "#2C251E",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      border:
                                        "1.5px solid rgba(197,165,114,0.6)",
                                      borderRadius: "8px",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "rgba(197,165,114,0.9)",
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "rgba(197,165,114,1)",
                                      },
                                    "& .MuiSvgIcon-root": {
                                      color: "#2C251E",
                                    },
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        backgroundColor: "#FEFEFE",
                                        color: "#2C251E",
                                        borderRadius: "10px",
                                        marginTop: "10px",
                                        border:
                                          "1.5px solid rgba(197,165,114,0.6)",
                                        "& .MuiMenuItem-root": {
                                          fontSize: "14px",
                                          borderRadius: "6px",
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(197,165,114,0.2)",
                                          },
                                        },

                                        "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":
                                          {
                                            backgroundColor:
                                              "rgba(44,37,30,0.9) !important",
                                            color: "#FEFEFE !important",
                                            borderRadius: "0px !important",
                                            "& .MuiListItemButton-root": {
                                              borderRadius: "0px !important",
                                            },
                                          },

                                        "& .MuiList-root .Mui-selected": {
                                          borderRadius: "0px !important",
                                        },

                                        "& .MuiMenuItem-root:first-of-type": {
                                          borderTopLeftRadius: "6px",
                                          borderTopRightRadius: "6px",
                                        },

                                        "& .MuiMenuItem-root:last-of-type": {
                                          borderBottomLeftRadius: "6px",
                                          borderBottomRightRadius: "6px",
                                        },
                                      },
                                    },
                                  }}
                                >
                                  <MenuItem value="7">7 days</MenuItem>
                                  <MenuItem value="30">30 days</MenuItem>
                                  <MenuItem value="365">1 year</MenuItem>
                                  <MenuItem value="all">All time</MenuItem>
                                </Selected>
                              </FormControl>
                            )}
                          </Stack>

                          {userDetails?.role !== "Patient" ? (
                            <Typography
                              variant="h2"
                              sx={{
                                padding: "15px 10px",
                                fontSize: "24px",
                                // backgroundColor: "aqua",
                              }}
                            >
                              {userDataLoading ? (
                                <CircularProgress size={24} sx={{ color: "#C5A572" }} />
                              ) : totalAppointments ? (
                                totalAppointments
                              ) : (
                                "Not Available"
                              )}
                            </Typography>
                          ) : (
                            <Typography
                              variant="h2"
                              sx={{
                                padding: "15px 10px",
                                fontSize: "24px",
                              }}
                            >
                              {userDataLoading ? (
                                <CircularProgress size={24} sx={{ color: "#C5A572" }} />
                              ) : nextAppointment ? (
                                formatDateMMDDYYYY(nextAppointment.date)
                              ) : (
                                "Not Available"
                              )}
                            </Typography>
                          )}
                          </>
                          )}
                        </CardContent>
                      </Box>
                    </Grid>

                    {/* Treatment Duration && Total Income && Pending Appointments*/}
                    {userDetails?.role !== "ClinicStaff" && (
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }} item>
                      <Box
                        p={2}
                        border="1px solid #0000010ff"
                        backgroundColor="#fefefe"
                        sx={{
                          // padding: "5px 0",
                          borderRadius: "16px",
                          boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
                          // border: "1px solid rgba(89, 84, 74, .20)",
                        }}
                      >
                        <CardContent
                          sx={{
                            padding: "20px",
                          }}
                        >
                          <Stack
                            direction="row"
                            sx={{
                              // backgroundColor: "aqua",
                              justifyContent: "space-between",
                              position: "relative",
                              alignItems: "center",
                            }}
                          >
                            <Stack
                              direction="row"
                              sx={{
                                width: "100%",
                                justifyContent:
                                  userDetails?.role === "Patient"
                                    ? "space-between"
                                    : null,
                                alignContent: "center",
                                alignItems: "center",
                                // backgroundColor: "mediumaquamarine",
                                textAlign: "center",
                                justifyItems: "center",
                              }}
                            >
                              {userDetails?.role === "DentistOwner" ? (
                                <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  // border: "1px solid black",
                  borderRadius: "8px",
                  backgroundColor: "#2E2D2D",
                  boxShadow: '0 1px 5px rgba(0,0,0,0.3)',
                }}>
                                  <Icon src={money} size="small" />
                                </Box>
                              ) : userDetails?.role === "AssociateDentist" ? (
                                <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  // border: "1px solid black",
                  borderRadius: "8px",
                  backgroundColor: "#2E2D2D",
                  boxShadow: '0 1px 5px rgba(0,0,0,0.3)',
                }}>
                                  <Icon src={pending} size="small" />
</Box>
                              ) : userDetails?.role === "ClinicStaff" ? (
                                <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  // border: "1px solid black",
                  borderRadius: "8px",
                  backgroundColor: "#2E2D2D",
                  boxShadow: '0 1px 5px rgba(0,0,0,0.3)',
                }}>
                                  <Icon src={pending} size="small" />
                                </Box>
                              ) : null}

                              <Text
                                text={
                                  userDetails?.role === "Patient"
                                    ? "Treatment Duration"
                                    : userDetails?.role === "DentistOwner"
                                    ? "Total Income"
                                    : (userDetails?.role === "AssociateDentist" || userDetails?.role === "ClinicStaff")
                                    ? "Pending Appointments"
                                    : "Total Appointments"
                                }
                                variant="h3Header"
                              />

                              {userDetails?.role === "Patient" && (
                                <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  // border: "1px solid black",
                  borderRadius: "8px",
                  backgroundColor: "#2E2D2D",
                  boxShadow: '0 1px 5px rgba(0,0,0,0.3)',
                }}>
                                <Icon src={clock} size="small" /></Box>
                              )}
                            </Stack>

                            {userDetails?.role === "DentistOwner" && (
                              <FormControl
                                sx={{
                                  maxWidth: "100px",
                                  minWidth: "100px",
                                }}
                              >
                                <Selected
                                  labelId="demo-simple-select-label"
                                  id="demo-simple-select"
                                  value={incomeRangeType}
                                  onChange={(e) =>
                                    setIncomeRangeType(e.target.value)
                                  }
                                  sx={{
                                    maxWidth: "150px",
                                    fontSize: "14px",
                                    "& .MuiSelect-select": {
                                      padding: "10px 15px",
                                      color: "#2C251E",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      border:
                                        "1.5px solid rgba(197,165,114,0.6)",
                                      borderRadius: "8px",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "rgba(197,165,114,0.9)",
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "rgba(197,165,114,1)",
                                      },
                                    "& .MuiSvgIcon-root": {
                                      color: "#2C251E",
                                    },
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        backgroundColor: "#FEFEFE",
                                        color: "#2C251E",
                                        borderRadius: "10px",
                                        marginTop: "10px",
                                        border:
                                          "1.5px solid rgba(197,165,114,0.6)",

                                        "& .MuiMenuItem-root": {
                                          fontSize: "14px",
                                          borderRadius: "6px",
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(197,165,114,0.2)",
                                          },
                                        },

                                        "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":
                                          {
                                            backgroundColor:
                                              "rgba(44,37,30,0.9) !important",
                                            color: "#FEFEFE !important",
                                            borderRadius: "0px !important",
                                            "& .MuiListItemButton-root": {
                                              borderRadius: "0px !important",
                                            },
                                          },

                                        "& .MuiList-root .Mui-selected": {
                                          borderRadius: "0px !important",
                                        },

                                        "& .MuiMenuItem-root:first-of-type": {
                                          borderTopLeftRadius: "6px",
                                          borderTopRightRadius: "6px",
                                        },

                                        "& .MuiMenuItem-root:last-of-type": {
                                          borderBottomLeftRadius: "6px",
                                          borderBottomRightRadius: "6px",
                                        },
                                      },
                                    },
                                  }}
                                >
                                  <MenuItem value="7">7 days</MenuItem>
                                  <MenuItem value="30">30 days</MenuItem>
                                  <MenuItem value="365">1 year</MenuItem>
                                  <MenuItem value="all">All time</MenuItem>
                                </Selected>
                              </FormControl>
                            )}

                            {userDetails?.role === "ClinicStaff" ? (
                              <FormControl
                                sx={{
                                  maxWidth: "100px",
                                  minWidth: "100px",
                                }}
                              >
                                <Selected
                                  labelId="demo-simple-select-label"
                                  id="demo-simple-select"
                                  value={pendingRangeType}
                                  onChange={(e) =>
                                    setPendingRangeType(e.target.value)
                                  }
                                  sx={{
                                    maxWidth: "150px",
                                    fontSize: "14px",
                                    "& .MuiSelect-select": {
                                      padding: "10px 15px",
                                      color: "#2C251E",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      border:
                                        "1.5px solid rgba(197,165,114,0.6)",
                                      borderRadius: "8px",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "rgba(197,165,114,0.9)",
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "rgba(197,165,114,1)",
                                      },
                                    "& .MuiSvgIcon-root": {
                                      color: "#2C251E",
                                    },
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        backgroundColor: "#FEFEFE",
                                        color: "#2C251E",
                                        borderRadius: "10px",
                                        marginTop: "10px",
                                        border:
                                          "1.5px solid rgba(197,165,114,0.6)",

                                        "& .MuiMenuItem-root": {
                                          fontSize: "14px",
                                          borderRadius: "6px",
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(197,165,114,0.2)",
                                          },
                                        },

                                        "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":
                                          {
                                            backgroundColor:
                                              "rgba(44,37,30,0.9) !important",
                                            color: "#FEFEFE !important",
                                            borderRadius: "0px !important",
                                            "& .MuiListItemButton-root": {
                                              borderRadius: "0px !important",
                                            },
                                          },

                                        "& .MuiList-root .Mui-selected": {
                                          borderRadius: "0px !important",
                                        },

                                        "& .MuiMenuItem-root:first-of-type": {
                                          borderTopLeftRadius: "6px",
                                          borderTopRightRadius: "6px",
                                        },

                                        "& .MuiMenuItem-root:last-of-type": {
                                          borderBottomLeftRadius: "6px",
                                          borderBottomRightRadius: "6px",
                                        },
                                      },
                                    },
                                  }}
                                >
                                  <MenuItem value="7">7 days</MenuItem>
                                  <MenuItem value="30">30 days</MenuItem>
                                  <MenuItem value="365">1 year</MenuItem>
                                  <MenuItem value="all">All time</MenuItem>
                                </Selected>
                              </FormControl>
                            ) : userDetails?.role === "AssociateDentist" ? (
                              <FormControl
                                sx={{
                                  maxWidth: "100px",
                                  minWidth: "100px",
                                }}
                              >
                                <Selected
                                  labelId="demo-simple-select-label"
                                  id="demo-simple-select"
                                  value={pendingRangeType}
                                  onChange={(e) =>
                                    setPendingRangeType(e.target.value)
                                  }
                                  sx={{
                                    maxWidth: "150px",
                                    fontSize: "14px",
                                    "& .MuiSelect-select": {
                                      padding: "10px 15px",
                                      color: "#2C251E",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      border:
                                        "1.5px solid rgba(197,165,114,0.6)",
                                      borderRadius: "8px",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "rgba(197,165,114,0.9)",
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "rgba(197,165,114,1)",
                                      },
                                    "& .MuiSvgIcon-root": {
                                      color: "#2C251E",
                                    },
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        backgroundColor: "#FEFEFE",
                                        color: "#2C251E",
                                        borderRadius: "10px",
                                        marginTop: "10px",
                                        border:
                                          "1.5px solid rgba(197,165,114,0.6)",

                                        "& .MuiMenuItem-root": {
                                          fontSize: "14px",
                                          borderRadius: "6px",
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(197,165,114,0.2)",
                                          },
                                        },

                                        "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":
                                          {
                                            backgroundColor:
                                              "rgba(44,37,30,0.9) !important",
                                            color: "#FEFEFE !important",
                                            borderRadius: "0px !important",
                                            "& .MuiListItemButton-root": {
                                              borderRadius: "0px !important",
                                            },
                                          },

                                        "& .MuiList-root .Mui-selected": {
                                          borderRadius: "0px !important",
                                        },

                                        "& .MuiMenuItem-root:first-of-type": {
                                          borderTopLeftRadius: "6px",
                                          borderTopRightRadius: "6px",
                                        },

                                        "& .MuiMenuItem-root:last-of-type": {
                                          borderBottomLeftRadius: "6px",
                                          borderBottomRightRadius: "6px",
                                        },
                                      },
                                    },
                                  }}
                                >
                                  <MenuItem value="7">7 days</MenuItem>
                                  <MenuItem value="30">30 days</MenuItem>
                                  <MenuItem value="365">1 year</MenuItem>
                                  <MenuItem value="all">All time</MenuItem>
                                </Selected>
                              </FormControl>
                            ) : null}
                          </Stack>

                          {userDetails?.role === "DentistOwner" ? (
                            <Typography
                              variant="h2"
                              sx={{
                                padding: "15px 10px",
                                fontSize: "24px",
                                // backgroundColor: "aqua",
                              }}
                            >
                              {totalIncome
                                ? totalIncome.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                  })
                                : "No income"}
                            </Typography>
                          ) : userDetails?.role === "ClinicStaff" ||
                            userDetails?.role === "AssociateDentist" ? (
                            <Typography
                              variant="h2"
                              sx={{
                                padding: "15px 10px",
                                fontSize: "24px",
                                // backgroundColor: "aqua",
                              }}
                            >
                              {totalPendingAppointments}
                            </Typography>
                          ) : (
                            <Typography
                              variant="h2"
                              sx={{
                                padding: "15px 10px",
                                fontSize: "24px",
                              }}
                            >
                              {userDataLoading ? (
                                <CircularProgress size={24} sx={{ color: "#C5A572" }} />
                              ) : nextAppointment && nextAppointment.duration ? (
                                formatDuration(nextAppointment.duration)
                              ) : (
                                "Not Available"
                              )}
                            </Typography>
                          )}
                        </CardContent>
                      </Box>
                    </Grid>
                    )}

                    {/* Treatment Status && Total Patients*/}
                    {userDetails?.role !== "ClinicStaff" && (
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 4 }} item>
                      <Box
                        p={2}
                        border="1px solid #0000010ff"
                        backgroundColor="#fefefe"
                        sx={{
                          // padding: "5px 0",
                          borderRadius: "16px",
                          boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
                          // border: "1px solid rgba(89, 84, 74, .20)",
                        }}
                      >

                        <CardContent
                          sx={{
                            padding:
                              !newPatients && !returningPatients
                                ? "20px"
                                : "0 20px",
                          
                          alignItems: "center",
                          }}
                        >

                          <Stack
                            direction="row"
                            sx={{
                              justifyContent: "space-between",
                              alignItems: "center",
                          
                            }}
                          >
                            <Stack
                              direction="row"
                              sx={{
                                width: "100%",
                                justifyContent:
                                  userDetails?.role === "Patient"
                                    ? "space-between"
                                    : null,
                                alignContent: "center",
                                alignItems: "center",
                                textAlign: "center",
                                justifyItems: "center",
                              }}
                            >
                              {userDetails?.role !== "Patient" && (
                                <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  // border: "1px solid black",
                  borderRadius: "8px",
                  backgroundColor: "#2E2D2D",
                  boxShadow: '0 1px 5px rgba(0,0,0,0.3)',
                }}>
                                <Icon src={patients} size="small" />
                                </Box>
                              )}

                              <Text
                                text={
                                  userDetails?.role === "Patient"
                                    ? "Treatment Status"
                                    : "Total Patients"
                                }
                                variant="h3Header"
                              />

                              {userDetails?.role === "Patient" && (
                                <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  // border: "1px solid black",
                  borderRadius: "8px",
                  backgroundColor: "#2E2D2D",
                  boxShadow: '0 1px 5px rgba(0,0,0,0.3)',
                }}>
                                <Icon src={status} size="small" /></Box>
                              )}
                            </Stack>

                            {userDetails?.role !== "Patient" && (
                              <FormControl
                                sx={{
                                  maxWidth: "100px",
                                  minWidth: "100px",
                                }}
                              >
                                <Selected
                                  labelId="demo-simple-select-label"
                                  id="demo-simple-select"
                                  value={patientsRangeType}
                                  onChange={(e) =>
                                    setPatientsRangeType(e.target.value)
                                  }
                                  sx={{
                                    maxWidth: "150px",
                                    fontSize: "14px",
                                    "& .MuiSelect-select": {
                                      padding: "10px 15px",
                                      color: "#2C251E",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      border:
                                        "1.5px solid rgba(197,165,114,0.6)",
                                      borderRadius: "8px",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "rgba(197,165,114,0.9)",
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "rgba(197,165,114,1)",
                                      },
                                    "& .MuiSvgIcon-root": {
                                      color: "#2C251E",
                                    },
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        backgroundColor: "#FEFEFE",
                                        color: "#2C251E",
                                        borderRadius: "10px",
                                        marginTop: "10px",
                                        border:
                                          "1.5px solid rgba(197,165,114,0.6)",

                                        "& .MuiMenuItem-root": {
                                          fontSize: "14px",
                                          borderRadius: "6px",
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(197,165,114,0.2)",
                                          },
                                        },

                                        "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":
                                          {
                                            backgroundColor:
                                              "rgba(44,37,30,0.9) !important",
                                            color: "#FEFEFE !important",
                                            borderRadius: "0px !important",
                                            "& .MuiListItemButton-root": {
                                              borderRadius: "0px !important",
                                            },
                                          },

                                        "& .MuiList-root .Mui-selected": {
                                          borderRadius: "0px !important",
                                        },

                                        "& .MuiMenuItem-root:first-of-type": {
                                          borderTopLeftRadius: "6px",
                                          borderTopRightRadius: "6px",
                                        },

                                        "& .MuiMenuItem-root:last-of-type": {
                                          borderBottomLeftRadius: "6px",
                                          borderBottomRightRadius: "6px",
                                        },
                                      },
                                    },
                                  }}
                                >
                                  <MenuItem value="7">7 days</MenuItem>
                                  <MenuItem value="30">30 days</MenuItem>
                                  <MenuItem value="365">1 year</MenuItem>
                                  <MenuItem value="all">All time</MenuItem>
                                </Selected>
                              </FormControl>
                            )}
                          </Stack>

                          {userDetails?.role !== "Patient" ? (
                            <>
                              {newPatients || returningPatients ? (
                                <Box
                                  sx={{
                                    margin: "7px 0 0 0",
                                    borderLeft: "2px solid black",
                                    borderRight: "2px solid black",
                                  }}
                                >
                                  <Stack direction="row" width="100%">
                                    <Stack
                                      direction="column"
                                      sx={{
                                        width:
                                          newPatients /
                                          (returningPatients + newPatients),
                                        minWidth: "35px",
                                        borderRight: "2px solid black",
                                        padding: "0 8px",
                                        overflowX: "hidden",
                                        transition: "width 0.5s",
                                      }}
                                    >
                                      <Typography
                                        variant="h2"
                                        sx={{
                                          padding: "2px 0",
                                          fontSize: "17px",
                                          // backgroundColor: "aqua",
                                        }}
                                      >
                                        {newPatients}
                                      </Typography>
                                      <Typography
                                        variant="h2"
                                        sx={{
                                          padding: "2px 0",
                                          fontSize: "17px",
                                          fontWeight: 100,
                                          // backgroundColor: "aqua",
                                        }}
                                      >
                                        New
                                      </Typography>
                                      <Box
                                        sx={{
                                          height: "15px",
                                          backgroundColor: "#C5A572",
                                        }}
                                      ></Box>
                                    </Stack>

                                    <Stack
                                      direction="column"
                                      sx={{
                                        width:
                                          returningPatients /
                                          (returningPatients + newPatients),
                                        minWidth: "52px",
                                        padding: "0 8px",
                                        overflowX: "hidden",
                                        transition: "width 0.5s",
                                      }}
                                    >
                                      <Typography
                                        variant="h2"
                                        sx={{
                                          padding: "2px 0",
                                          fontSize: "17px",
                                        }}
                                      >
                                        {returningPatients}
                                      </Typography>
                                      <Typography
                                        variant="h2"
                                        sx={{
                                          padding: "2px 0",
                                          fontSize: "17px",
                                          fontWeight: 100,
                                        }}
                                      >
                                        {returningPatients /
                                          (returningPatients + newPatients) <
                                        0.3
                                          ? "Return"
                                          : "Returning"}
                                      </Typography>
                                      <Box
                                        sx={{
                                          height: "15px",
                                          backgroundColor: "#C5A572",
                                        }}
                                      ></Box>
                                    </Stack>
                                  </Stack>
                                </Box>
                              ) : (
                                <>
                                  <Typography
                                    variant="h2"
                                    sx={{
                                      padding: 
                                        newPatients && returningPatients
                                          ? "15px 10px"
                                          : "15px 10px",
                                      fontSize: "24px",
                                    }}
                                  >
                                    No patients.
                                  </Typography>
                                </>
                              )}
                            </>
                          ) : (
                            <Box sx={{ padding: "15px 10px", display: "flex", alignItems: "center" }}>
                              {userDataLoading ? (
                                <CircularProgress size={24} sx={{ color: "#C5A572" }} />
                              ) : (
                                <Typography
                                  sx={{
                                    paddingY: "2px",
                                    fontWeight: "bold",
                                    fontSize: "24px",
                                    color:
                                      nextAppointment?.status === "Confirmed"
                                        ? "#558363"
                                        : nextAppointment?.status === "Pending"
                                        ? "#AD8C60"
                                        : "#2C251E",
                                  }}
                                >
                                  {nextAppointment ? nextAppointment.status : "Not Available"}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </Box>
                    </Grid>
                    )}
                  </Grid>)}

                  {/* Bottom Widgets */}
                  <Grid
                    container
                    spacing={3}
                    sx={{
                      padding: "20px",
                      paddingTop: "10px",
                      // backgroundColor: "blue",
                      "& .MuiGrid-item": {
                        display: "flex",
                        justifyContent: "center",
                        // backgroundColor: "blue",
                      },
                    }}
                  >
                    {/* Upcoming Appointments && Latest Appointments*/}
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: userDetails?.role === "ClinicStaff" ? 12 : 6 }} item>
                      <Box
                        p={userDetails?.role === "ClinicStaff" ? 0 : 2}
                        border={userDetails?.role === "ClinicStaff" ? "none" : "1px solid #0000010ff"}
                        backgroundColor={userDetails?.role === "ClinicStaff" ? "transparent" : "#fefefe"}
                        sx={{
                          // padding: "5px 0",
                          borderRadius: userDetails?.role === "ClinicStaff" ? 0 : "16px",
                          boxShadow: userDetails?.role === "ClinicStaff" ? "none" : '0 1px 5px rgba(0,0,0,0.4)',
                          // border: "1px solid rgba(89, 84, 74, .20)",
                          height:
                          userDetails?.role === "ClinicStaff"
                            ? "auto"
                            : userDetails?.role !== "Patient"
                              ? {
                            lg : latestAppointments.length === 0 ? "100%" :
                            latestAppointments.length === 1 ? "100%" : 
                            latestAppointments.length === 2 ? "100%" : 
                            "525px",
                            xl : "525px",
                          } : {
                            lg : latestPatientAppointments.length === 0 ? "100%" :
                            latestPatientAppointments.length === 1 ? "100%" : 
                            latestPatientAppointments.length === 2 ? "100%" : 
                            "525px",
                            xl : "525px",
                          },           
                          minHeight: userDetails?.role === "ClinicStaff" ? "525px" : undefined,
                          boxSizing: "border-box",
                        }}
                      >
                        <CardContent
                          sx={{
                            padding: userDetails?.role === "ClinicStaff" ? "0" : "20px",
                            height: "100%",          // ensure content fills outer fixed height
                            boxSizing: "border-box",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {userDetails?.role === "ClinicStaff" ? (
                            <ClinicStaffDashboardCalendar
                              statsCard={
                                <Box sx={{ borderRadius: "12px", border: "1px solid rgba(197,165,114,0.35)", backgroundColor: "white", padding: "12px 16px", alignSelf: "flex-start" }}>
                                  <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1 }}>
                                    <Box sx={{ width: "28px", height: "28px", borderRadius: "6px", backgroundColor: "#2E2D2D", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                      <Icon src={activeStatsWidget === "appointments" ? appointments : activeStatsWidget === "pending" ? pending : patients} size="small" />
                                    </Box>
                                    <Selected
                                      value={activeStatsWidget}
                                      onChange={(e) => setActiveStatsWidget(e.target.value)}
                                      variant="standard"
                                      disableUnderline
                                      sx={{ "& .MuiSelect-select": { padding: "0 20px 0 0", fontWeight: 700, fontSize: "13px", color: "#2C251E" }, "& .MuiSvgIcon-root": { color: "#C5A572", fontSize: "18px" }, "&:before": { display: "none" }, "&:after": { display: "none" } }}
                                      MenuProps={{ PaperProps: { sx: { backgroundColor: "#FEFEFE", color: "#2C251E", borderRadius: "10px", marginTop: "10px", border: "1.5px solid rgba(197,165,114,0.6)", "& .MuiMenuItem-root": { fontSize: "13px", borderRadius: "6px", "&:hover": { backgroundColor: "rgba(197,165,114,0.2)" } }, "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: "rgba(44,37,30,0.9) !important", color: "#FEFEFE !important" } } } }}
                                    >
                                      <MenuItem value="appointments">Total Appointments</MenuItem>
                                      <MenuItem value="pending">Pending Appointments</MenuItem>
                                      <MenuItem value="patients">Total Patients</MenuItem>
                                    </Selected>
                                    <Selected
                                      value={activeStatsWidget === "appointments" ? appointmentsRangeType : activeStatsWidget === "pending" ? pendingRangeType : patientsRangeType}
                                      onChange={(e) => {
                                        if (activeStatsWidget === "appointments") setAppointmentsRangeType(e.target.value);
                                        else if (activeStatsWidget === "pending") setPendingRangeType(e.target.value);
                                        else setPatientsRangeType(e.target.value);
                                      }}
                                      size="small"
                                      sx={{ fontSize: "12px", "& .MuiSelect-select": { padding: "4px 28px 4px 8px", color: "#2C251E", fontSize: "12px" }, "& .MuiOutlinedInput-notchedOutline": { border: "1px solid rgba(197,165,114,0.6)", borderRadius: "8px" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(197,165,114,0.9)" }, "& .MuiSvgIcon-root": { color: "#2C251E", fontSize: "18px" } }}
                                      MenuProps={{ PaperProps: { sx: { backgroundColor: "#FEFEFE", color: "#2C251E", borderRadius: "10px", marginTop: "10px", border: "1.5px solid rgba(197,165,114,0.6)", "& .MuiMenuItem-root": { fontSize: "13px", borderRadius: "6px", "&:hover": { backgroundColor: "rgba(197,165,114,0.2)" } }, "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover": { backgroundColor: "rgba(44,37,30,0.9) !important", color: "#FEFEFE !important" } } } }}
                                    >
                                      <MenuItem value="7">7 days</MenuItem>
                                      <MenuItem value="30">30 days</MenuItem>
                                      <MenuItem value="365">1 year</MenuItem>
                                      <MenuItem value="all">All time</MenuItem>
                                    </Selected>
                                  </Stack>
                                  {activeStatsWidget === "patients" ? (
                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 0.5 }}>
                                      <Box>
                                        <Typography sx={{ fontSize: "11px", color: "#888", fontWeight: 500 }}>New</Typography>
                                        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#2C251E", lineHeight: 1.2 }}>
                                          {userDataLoading ? <CircularProgress size={18} sx={{ color: "#C5A572" }} /> : (newPatients || 0)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ width: "1px", height: 32, backgroundColor: "rgba(197,165,114,0.5)" }} />
                                      <Box>
                                        <Typography sx={{ fontSize: "11px", color: "#888", fontWeight: 500 }}>Returning</Typography>
                                        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#C5A572", lineHeight: 1.2 }}>
                                          {userDataLoading ? <CircularProgress size={18} sx={{ color: "#C5A572" }} /> : (returningPatients || 0)}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  ) : (
                                    <Typography sx={{ px: 0.5, fontSize: "20px", fontWeight: 700, color: "#2C251E", lineHeight: 1.2 }}>
                                      {userDataLoading ? (
                                        <CircularProgress size={18} sx={{ color: "#C5A572" }} />
                                      ) : activeStatsWidget === "appointments" ? (
                                        totalAppointments || "Not Available"
                                      ) : (
                                        totalPendingAppointments || "0"
                                      )}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          ) : (
                          <>
                          <Stack
                            direction={{
                              xs: "column",
                              sm: "row",
                              md: "row",
                              lg: "row",
                              xl: "row",
                            }}
                            sx={{
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Stack
                              direction="row"
                              sx={{
                                alignItems: "center",
                                width: "100%"
                              }}>
                            <Box
                              sx={{
                                width: "100%"
                              }}>
                            <Typography
                              gutterBottom
                              variant="h3"
                              component="div"
                              sx={{
                                fontWeight: "600",
                                padding: "15px 10px",
                                fontSize: "24px",
                                marginBottom: "0px",
                              }}
                            >
                              {userDetails?.role !== "Patient"
                                ? "Latest Appointments"
                                : "Upcoming Appointments"}
                            </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: {
                                  xs: "block",
                                  sm: "none",
                                  md: "none",
                                  lg: "none",
                                  xl: "none",
                                }
                              }}>
                             <CustomWidthTooltip
                                title={
                                  userDetails?.role !== "Patient"
                                    ? "The top three latest appointments booked."
                                    : "All your upcoming appointments."
                                }
                                placement="top-end"
                                arrow
                                sx={{
                                  maxWidth: 200,
                                }}
                              >
                                <Box
                                  onMouseEnter={() =>
                                    setIconSrc(
                                      require("../../assets/icons/help_dark.png")
                                    )
                                  }
                                  onMouseLeave={() =>
                                    setIconSrc(
                                      require("../../assets/icons/help.png")
                                    )
                                  }
                                  sx={{
                                    width: "40px",
                                    height: "40px",
                                  }}
                                >
                                  <Icon src={iconSrc} size="small" />
                                </Box>
                              </CustomWidthTooltip>
                            </Box>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center" 
                              sx={{
                                paddingTop: {
                                  xs: "10px",
                                  sm: 0,
                                  md: 0,
                                  lg: 0,
                                  xl: 0,
                                }, 
                                width: {
                                  xs: "100%",
                                  sm: "auto"
                                }
                              }}>
                              {/* Dentist filter dropdown - only for DentistOwner and ClinicStaff */}
                              {userDetails?.role !== "Patient" && 
                               (userDetails?.role === "DentistOwner" || userDetails?.role === "ClinicStaff") && (
                                <FormControl
                                  sx={{
                                    minWidth: "150px",
                                    width: {
                                      xs: "100%",
                                      sm: "0",
                                      md: "0",
                                      lg: "0",
                                      xl: "0",
                                    },
                                  }}
                                >
                                  <Selected
                                    value={selectedLatestDentist}
                                    onChange={(e) => setSelectedLatestDentist(e.target.value)}
                                    displayEmpty
                                    sx={{
                                      fontSize: "14px",
                                      "& .MuiSelect-select": {
                                        padding: "10px 15px",
                                        color: "#2C251E",
                                      },
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border: "1.5px solid rgba(197,165,114,0.6)",
                                        borderRadius: "8px",
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "rgba(197,165,114,0.9)",
                                      },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "rgba(197,165,114,1)",
                                      },
                                      "& .MuiSvgIcon-root": {
                                        color: "#2C251E",
                                      },
                                    }}
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          backgroundColor: "#FEFEFE",
                                          color: "#2C251E",
                                          borderRadius: "10px",
                                          marginTop: "10px",
                                          border: "1.5px solid rgba(197,165,114,0.6)",
                                          maxHeight: "300px",
                                          "& .MuiMenuItem-root": {
                                            fontSize: "14px",
                                            borderRadius: "6px",
                                            "&:hover": {
                                              backgroundColor: "rgba(197,165,114,0.2)",
                                            },
                                          },
                                          "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover": {
                                            backgroundColor: "rgba(44,37,30,0.9) !important",
                                            color: "#FEFEFE !important",
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem value="all">All Dentists</MenuItem>
                                    {dentistsList.map((dentist) => (
                                      <MenuItem key={dentist.id} value={dentist.id}>
                                        {dentist.name}
                                      </MenuItem>
                                    ))}
                                  </Selected>
                                </FormControl>
                              )}
                            <Box
                              sx={{
                                display: {
                                  xs: "none",
                                  sm: "block",
                                  md: "block",
                                  lg: "block",
                                  xl: "block",
                                }
                              }}>
                              <CustomWidthTooltip
                                title={
                                  userDetails?.role !== "Patient"
                                    ? "The top three latest appointments booked."
                                    : "All your upcoming appointments."
                                }
                                placement="top-end"
                                arrow
                                sx={{
                                  maxWidth: 200,
                                }}
                              >
                                <Box
                                  onMouseEnter={() =>
                                    setIconSrc(
                                      require("../../assets/icons/help_dark.png")
                                    )
                                  }
                                  onMouseLeave={() =>
                                    setIconSrc(
                                      require("../../assets/icons/help.png")
                                    )
                                  }
                                  sx={{
                                    width: "40px",
                                    height: "40px",
                                  }}
                                >
                                  <Icon src={iconSrc} size="small" />
                                </Box>
                              </CustomWidthTooltip>
                              </Box>
                            </Stack>
                          </Stack>

                          {/* Text */}
                          {userDetails?.role === "Patient" ? (
                            <>
                              {userDataLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                                  <CircularProgress sx={{ color: "#C5A572" }} />
                                </Box>
                              ) : latestPatientAppointments.length === 0 ? (
                                <Empty
                                  icon={require("../../assets/icons/calendar_dark.png")}
                                  header="No upcoming appointments"
                                  message="You don't have any scheduled visits at the moment. Book your next check-up to stay on track."
                                  buttonOn={true}/>
                              ) : (
                                // Containers
                                <Box sx={{ flex: 1, overflowY: "auto", height: "435px", pr: 2, boxSizing: "border-box" }}>
                                  {latestPatientAppointments.map(
                                    (appointment, idx) => {
                                      return (
                                              <Tooltip
                                                title={appointment?.status}
                                                placement="right"
                                                arrow
                                              >
                                        <List
                                          key={idx}
                                          sx={{
                                            width: "100%",
                                            marginTop: "10px",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "16px",
                                          }}
                                        >
                                          {/* Appointments */}
                                          <ListItem
                                            sx={{
                                              backgroundColor: appointment?.status === "Confirmed" ? "hsla(0, 1%, 18%, 0.90)" : "#FDF9F3",
                                              boxShadow:
                                                "0px 2px 5px rgba(0, 0, 0, 0.15)",
                                              borderRadius: "8px",
                                              padding: "20px",
                                            }}
                                          >
                                            {/* Icon */}
                                            <ListItemIcon
                                            sx={{
                                              display: {
                                                xs : "none",
                                                sm : "flex",
                                                md : "flex",
                                                lg : "flex",
                                                xl : "flex",
                                              }
                                              }}>
                                              <ListItemAvatar>
                                                <Avatar
                                                  sx={{
                                                    backgroundColor: appointment?.status === "Confirmed" ? "#FDF9F3" : "#2C251E",
                                                  }}
                                                >
                                                  <img
                                                    src={
                                                      appointment?.status === "Confirmed" ? require("../../assets/icons/confirmed.png") : require("../../assets/clock.png")
                                                    }
                                                    alt="logo"
                                                    className="dashboard-icons"
                                                  />
                                                </Avatar>
                                              </ListItemAvatar>
                                            </ListItemIcon>
                                            
                                            <Stack
                                              direction={{
                                                xs: "column",
                                                sm: "row",
                                                md: "row",
                                                lg: "row",
                                                xl: "row",
                                              }}
                                              width="100%"
                                              justifyContent="space-between"
                                              >

                                            <Stack
                                              direction="row"
                                              sx={{
                                                justifyContent: "space-between"
                                              }}>

                                            
                                            {/* Icon */}
                                            <ListItemIcon
                                            sx={{
                                              display: {
                                                xs : "flex",
                                                sm : "none",
                                                md : "none",
                                                lg : "none",
                                                xl : "none",
                                              }
                                              }}>
                                              <ListItemAvatar>
                                                <Avatar
                                                  sx={{
                                                    backgroundColor: "#2C251E",
                                                  }}
                                                >
                                                  <img
                                                    src={
                                                      appointment?.status === "Confirmed" ? require("../../assets/icons/ok.png") : require("../../assets/clock.png")
                                                    }
                                                    alt="logo"
                                                    className="dashboard-icons"
                                                  />
                                                </Avatar>
                                              </ListItemAvatar>
                                            </ListItemIcon>

                                            {/* Icon */}
                                              <ListItemIcon
                                                sx={{
                                              display: {
                                                xs : "flex",
                                                sm : "none",
                                                md : "none",
                                                lg : "none",
                                                xl : "none",
                                              },
                                              marginLeft: "10px" 
                                              }}
                                              >
                                                <ListItemAvatar>
                                                  <Avatar
                                                    sx={{
                                                      backgroundColor:
                                                        "transparent",
                                                      color: "#2C251E",
                                                    }}
                                                  >
                                                    <div
                                                      className="dropdown-container"
                                                      style={{
                                                        position: "relative",
                                                        // background: "rgba(0, 0, 0, 0.1)",
                                                        right: "0",
                                                      }}
                                                    >
                                                      <Box>
<Tooltip
        title="View Appointment Details"
        placement="bottom"
        arrow
      >
                                                        <Button
                                                          disableRipple
                                                          onClick={() =>
                                                                  handleAppointmentAction(
                                                                    appointment,
                                                                    "view"
                                                                  )
                                                                }
                                                          sx={{
                                                            // background: "rgba(0, 0, 0, 0.1)",
                                                            cursor: "pointer",
                                                            fontSize: "18px",
                                                            color: "black",
                                                            padding: "5px",
                                                            ":hover": {
                                                              background:
                                                                "rgba(0, 0, 0, 0.1)",
                                                            },
                                                          }}
                                                        >
                                                          ⋮
                                                        </Button>
                                                        </Tooltip>

                                                        {dropdownOpen ===
                                                          appointment.id && (
                                                          <div>
                                                            <Box
                                                              sx={{
                                                                position:
                                                                  "absolute",
                                                                // bottom: "10px",
                                                                right: "0",
                                                                background:
                                                                  "white",
                                                                border:
                                                                  "1px solid #ddd",
                                                                borderRadius:
                                                                  "5px",
                                                                boxShadow:
                                                                  "0 2px 5px rgba(0, 0, 0, 0.2)",
                                                                zIndex: 1000,
                                                                minWidth: "120px",
                                                              }}
                                                            >
                                                              <Button
                                                                onMouseEnter={(
                                                                  e
                                                                ) =>
                                                                  (e.target.style.background =
                                                                    "#f5f5f5")
                                                                }
                                                                onMouseLeave={(
                                                                  e
                                                                ) =>
                                                                  (e.target.style.background =
                                                                    "transparent")
                                                                }
                                                                onClick={() =>
                                                                  handleAppointmentAction(
                                                                    appointment,
                                                                    "view"
                                                                  )
                                                                }
                                                                disableRipple
                                                                sx={{
                                                                  width: "100%",
                                                                  padding:
                                                                    "8px 12px",
                                                                  border: "none",
                                                                  background:
                                                                    "transparent",
                                                                  cursor:
                                                                    "pointer",
                                                                  textAlign:
                                                                    "left",
                                                                  fontSize:
                                                                    "14px",
                                                                  color: "black",
                                                                  textTransform:
                                                                    "none",
                                                                }}
                                                              >
                                                                View Details
                                                              </Button>
                                                            </Box>
                                                          </div>
                                                        )}
                                                      </Box>
                                                    </div>
                                                  </Avatar>
                                                </ListItemAvatar>
                                              </ListItemIcon>
                                            </Stack>

                                              <Stack
                                                direction="row"
                                                width="100%">
                                              {/* Left Text  */}
                                              <ListItemText
                                                // Primary Text
                                                primary={
                                                  <Typography
                                                    sx={{
                                                      fontSize: "17px",
                                                      color: appointment?.status === "Confirmed" ? "#FDF9F3" : "#2C251E",
                                                      fontWeight: appointment?.status === "Confirmed" ? "300" : "600",
                                                      // backgroundColor: "cadetblue"
                                                    }}
                                                  >
                                                    {userDetails?.role !==
                                                    "Patient" ? (
                                                      <>{appointment.name}</>
                                                    ) : (
                                                      <>
                                                        {(() => {
                                                          const servicesArr =
                                                            Array.isArray(
                                                              appointment.services
                                                            )
                                                              ? appointment.services.map(
                                                                  (service) =>
                                                                    typeof service ===
                                                                      "object" &&
                                                                    service.name
                                                                      ? service.name
                                                                      : service
                                                                )
                                                              : [
                                                                  appointment.services ||
                                                                    "Service",
                                                                ];
                                                          const servicesStr =
                                                            servicesArr.join(
                                                              ", "
                                                            );
                                                          if (
                                                            servicesStr.length >=
                                                            25
                                                          ) {
                                                            const firstCommaIdx =
                                                              servicesStr.indexOf(
                                                                ","
                                                              );
                                                            if (
                                                              firstCommaIdx !== -1
                                                            ) {
                                                              return (
                                                                servicesStr.slice(
                                                                  0,
                                                                  firstCommaIdx
                                                                ) + ", ..."
                                                              );
                                                            } else {
                                                              return servicesStr.length >
                                                                25
                                                                ? servicesStr.slice(
                                                                    0,
                                                                    15
                                                                  ) + "..."
                                                                : servicesStr;
                                                            }
                                                          }
                                                          return servicesStr;
                                                        })()}
                                                      </>
                                                    )}
                                                  </Typography>
                                                }
                                                // Secondary Text
                                                secondary={
                                                  <Typography
                                                    sx={{
                                                      fontSize: "14px",
                                                      color: appointment?.status === "Confirmed" ? "#FDF9F3" : "rgba(44, 37, 30, .9)",
                                                    }}
                                                  >
                                                    {appointment.dentist}
                                                  </Typography>
                                                }
                                              />

                                              {/* Right Text */}
                                              <ListItemText
                                                primary={
                                                  <Typography
                                                    sx={{
                                                      fontWeight: appointment?.status === "Confirmed" ? "300" : "600",
                                                      fontSize: "17px",
                                                      textAlign: {
                                                        xs: "end",
                                                        sm: "end",
                                                        md: "end",
                                                        lg: "end",
                                                        xl: "end",
                                                      },
                                                      color: appointment?.status === "Confirmed" ? "#FDF9F3" :"#2C251E",
                                                    }}
                                                  >
                                                    {formatDateMMDDYYYY(appointment.date)}
                                                  </Typography>
                                                }
                                                secondary={
                                                  <Typography
                                                    sx={{
                                                      fontSize: "14px",
                                                      color: appointment?.status === "Confirmed" ? "#FDF9F3" : "rgba(44, 37, 30, .9)",
                                                      textAlign: {
                                                        xs: "end",
                                                        sm: "end",
                                                        md: "end",
                                                        lg: "end",
                                                        xl: "end",
                                                      },
                                                    }}
                                                  >
                                                    {(() => {
                                                      const startTime = appointment.startTime || appointment.time || "";
                                                      const endTime = appointment.endTime || "";
                                                      if (startTime && endTime) {
                                                        return `${formatTime12hr(startTime)} - ${formatTime12hr(endTime)}`;
                                                      } else if (startTime) {
                                                        return formatTime12hr(startTime);
                                                      }
                                                      return "N/A";
                                                    })()}
                                                  </Typography>
                                                }
                                              />
                                              </Stack>
                                            </Stack>


                                              {/* Icon */}
                                              <ListItemIcon
                                                sx={{
                                              display: {
                                                xs : "none",
                                                sm : "flex",
                                                md : "flex",
                                                lg : "flex",
                                                xl : "flex",
                                              },
                                              marginLeft: "10px" 
                                              }}
                                              >
                                                <ListItemAvatar>
                                                  <Avatar
                                                    sx={{
                                                      backgroundColor:
                                                        "transparent",
                                                      color: "#2C251E",
                                                    }}
                                                  >
                                                    <div
                                                      className="dropdown-container"
                                                      style={{
                                                        position: "relative",
                                                        // background: "rgba(0, 0, 0, 0.1)",
                                                        right: "0",
                                                      }}
                                                    >
                                                      <Box>
      <Tooltip
        title="View Appointment Details"
        placement="bottom"
        arrow
      >
                                                        <Button
                                                          disableRipple
                                                          onClick={() =>
                                                                  handleAppointmentAction(
                                                                    appointment,
                                                                    "view"
                                                                  )
                                                                }
                                                          sx={{
                                                            // background: "rgba(0, 0, 0, 0.1)",
                                                            cursor: "pointer",
                                                            fontSize: "18px",
                                                            color: appointment?.status === "Confirmed" ? "#FDF9F3" : "black",
                                                            padding: "5px",
                                                            ":hover": {
                                                              background:
                                                                "rgba(0, 0, 0, 0.1)",
                                                            },
                                                          }}
                                                        >
                                                          ⋮
                                                        </Button>
      </Tooltip>

                                                        
                                                      </Box>
                                                    </div>
                                                  </Avatar>
                                                </ListItemAvatar>
                                              </ListItemIcon>

                                            {/* Details Menu */}
                                            <div
                                              className="dropdown-container"
                                              style={{
                                                position: "relative",
                                                // background: "rgba(0, 0, 0, 0.1)",
                                                right: "30px",
                                                top: "25px",
                                              }}
                                            >
                                              <Box>
                                                {dropdownOpen ===
                                                  appointment.id && (
                                                  <div>
                                                    <Box
                                                      sx={{
                                                        position: "absolute",
                                                        right: "0",
                                                        background: "white",
                                                        border:
                                                          "1px solid #ddd",
                                                        borderRadius: "5px",
                                                        boxShadow:
                                                          "0 2px 5px rgba(0, 0, 0, 0.2)",
                                                        zIndex: 1000,
                                                        minWidth: "120px",
                                                      }}
                                                    >
                                                      <Button
                                                        onMouseEnter={(e) =>
                                                          (e.target.style.background =
                                                            "#f5f5f5")
                                                        }
                                                        onMouseLeave={(e) =>
                                                          (e.target.style.background =
                                                            "transparent")
                                                        }
                                                        onClick={() =>
                                                          handleAppointmentAction(
                                                            appointment,
                                                            "view"
                                                          )
                                                        }
                                                        disableRipple
                                                        sx={{
                                                          width: "100%",
                                                          padding: "8px 12px",
                                                          border: "none",
                                                          background:
                                                            "transparent",
                                                          cursor: "pointer",
                                                          textAlign: "left",
                                                          fontSize: "14px",
                                                          color: "black",
                                                          textTransform: "none",
                                                        }}
                                                      >
                                                        View Details
                                                      </Button>
                                                    </Box>
                                                  </div>
                                                )}
                                              </Box>
                                            </div>
                                          </ListItem>
                                        </List>
                                        </Tooltip>
                                      );
                                    }
                                  )}
                                </Box>
                              )}
                            </>
                          ) : (
                            <>
                              {userDataLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                                  <CircularProgress sx={{ color: "#C5A572" }} />
                                </Box>
                              ) : latestAppointments.length === 0 ? (
                                <Empty
                                  icon={require("../../assets/icons/document.png")}
                                  header="Oops looks empty here!"
                                  message={
                                    userDetails?.role === "Patient" ? "Schedule your first appointment and start your journey with us." : 
                                    "Your latest appointments will appear here."
                                  }/>
                              ) : (
                                // Containers
                                <Box sx={{ flex: 1, overflowY: "auto", height: "435px", pr: 2, boxSizing: "border-box" }}>
                                  {latestAppointments.map(
                                    (appointment, idx) => {
                                      return (
                                        <List
                                          key={idx}
                                          sx={{
                                            width: "100%",
                                            marginTop: "10px",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "16px",
                                          }}
                                        >
                                          {/* Appointments */}
                                          <ListItem
                                            sx={{
                                              backgroundColor: "#FDF9F3",
                                              boxShadow:
                                                "0px 2px 5px rgba(0, 0, 0, 0.15)",
                                              borderRadius: "8px",
                                              padding: "20px",
                                            }}
                                          >
                                            {/* Icon */}
                                            <ListItemIcon>
                                              <ListItemAvatar>
                                                <Avatar
                                                  sx={{
                                                    backgroundColor: "#2C251E",
                                                  }}
                                                >
                                                  <img
                                                    src={
                                                      appointment?.status === "Confirmed" 
                                                        ? require("../../assets/icons/ok.png") 
                                                        : clock
                                                    }
                                                    alt="logo"
                                                    className="dashboard-icons"
                                                  />
                                                </Avatar>
                                              </ListItemAvatar>
                                            </ListItemIcon>

                                            {/* Left Text */}
                                            <ListItemText
                                              primary={
                                                <Typography
                                                  sx={{
                                                    fontSize: "17px",
                                                    color: "#2C251E",
                                                    fontWeight: "600",
                                                  }}
                                                >
                                                  {appointment.name}
                                                </Typography>
                                              }
                                              secondary={
                                                <>
                                                  <Typography
                                                    sx={{
                                                      fontSize: "14px",
                                                      color: "#2C251E",
                                                    }}
                                                  >
                                                    {(() => {
                                                      const servicesArr =
                                                        Array.isArray(
                                                          appointment.service
                                                        )
                                                          ? appointment.service.map(
                                                              (service) =>
                                                                typeof service ===
                                                                  "object" &&
                                                                service.name
                                                                  ? service.name
                                                                  : service
                                                            )
                                                          : [
                                                              appointment.service ||
                                                                "Service",
                                                            ];
                                                      const servicesStr =
                                                        servicesArr.join(", ");
                                                      if (
                                                        servicesStr.length >= 25
                                                      ) {
                                                        const firstCommaIdx =
                                                          servicesStr.indexOf(
                                                            ","
                                                          );
                                                        if (
                                                          firstCommaIdx !== -1
                                                        ) {
                                                          return (
                                                            servicesStr.slice(
                                                              0,
                                                              firstCommaIdx
                                                            ) + ", ..."
                                                          );
                                                        } else {
                                                          return servicesStr.length >
                                                            25
                                                            ? servicesStr.slice(
                                                                0,
                                                                17
                                                              ) + "..."
                                                            : servicesStr;
                                                        }
                                                      }
                                                      return servicesStr;
                                                    })()}
                                                  </Typography>
                                                  <Typography
                                                    sx={{
                                                      fontSize: "14px",
                                                      color: "#59544A",
                                                      marginTop: "4px",
                                                    }}
                                                  >
                                                    {appointment.displayDate ||
                                                      formatDateMMDDYYYY(
                                                        appointment._dateKey ||
                                                          appointment.date ||
                                                          ""
                                                      )}
                                                  </Typography>
                                                </>
                                              }
                                            />

                                            {/* Right Text */}
                                            <ListItemText
                                              primary={
                                                <Typography
                                                  sx={{
                                                    fontWeight: "600",
                                                    fontSize: "17px",
                                                    textAlign: "end",
                                                    color:
                                                      appointment?.status ===
                                                      "Confirmed"
                                                        ? "#558363"
                                                        : appointment?.status ===
                                                          "Pending"
                                                        ? "#AD8C60"
                                                        : "#2C251E",
                                                  }}
                                                >
                                                  {appointment.status}
                                                </Typography>
                                              }
                                              secondary={
                                                <Typography
                                                  sx={{
                                                    fontSize: "14px",
                                                    color: "#59544A",
                                                    textAlign: "end",
                                                  }}
                                                >
                                                  {formatTime12hr(
                                                    appointment.time || ""
                                                  ) || "N/A"}
                                                </Typography>
                                              }
                                            />
                                          </ListItem>
                                        </List>
                                      );
                                    }
                                  )}
                                </Box>
                              )}
                            </>
                          )}
                          </>
                          )}
                        </CardContent>
                      </Box>
                    </Grid>

                    {/* Treatment History && Popular Service */}
                    {userDetails?.role !== "ClinicStaff" && (
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }} item>
                      <Box
                        p={2}
                        border="1px solid #0000010ff"
                        backgroundColor="#fefefe"
                        overflowY="auto"
                        sx={{
                          // padding: "5px 0",
                          borderRadius: "16px",
                          boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
                          // border: "1px solid rgba(89, 84, 74, .20)",
                          height: 
                          userDetails?.role !== "Patient" ? {
                            lg : popularServices.length === 0 ? "100%" :
                            popularServices.length === 1 ? "100%" : 
                            popularServices.length === 2 ? "100%" : 
                            "525px",
                            xl : "525px",
                          } : {
                            lg : treatmentHistory.length === 0 ? "100%" :
                            treatmentHistory.length === 1 ? "100%" : 
                            treatmentHistory.length === 2 ? "100%" : 
                            "525px",
                            xl : "525px",
                          },  
                          boxSizing: "border-box",
                        }}
                      >
                        <CardContent
                          sx={{
                            padding: "20px",
                            height: "100%",          
                            boxSizing: "border-box",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Stack
                            direction={{
                              xs: "column",
                              sm: "row",
                              md: "row",
                              lg: "row",
                              xl: "row",
                            }}
                            sx={{
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Stack
                              direction="row"
                              sx={{
                                alignItems: "center",
                                width: "100%"
                              }}>
                            <Box
                              sx={{
                                width: "100%"
                              }}>
                            <Typography
                              gutterBottom
                              variant="h3"
                              component="div"
                              sx={{
                                fontWeight: "600",
                                padding: "15px 10px",
                                fontSize: "24px",
                                // backgroundColor: "aqua",
                                marginBottom: "0px",
                              }}
                            >
                              {userDetails?.role !== "Patient"
                                ? "Popular Services"
                                : "Treatment History"}
                            </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: {
                                  xs: "block",
                                  sm: "none",
                                  md: "none",
                                  lg: "none",
                                  xl: "none",
                                }
                              }}>
                              <CustomWidthTooltipHistory
                                title={
                                  userDetails?.role !== "Patient"
                                    ? "The top three most popular booked service."
                                    : "The top three latest completed appointment."
                                }
                                placement="top-end"
                                arrow
                                sx={{
                                  maxWidth: 200,
                                }}
                              >
                                <Box
                                  onMouseEnter={() =>
                                    setIconSrcHistory(
                                      require("../../assets/icons/help_dark.png")
                                    )
                                  }
                                  onMouseLeave={() =>
                                    setIconSrcHistory(
                                      require("../../assets/icons/help.png")
                                    )
                                  }
                                  sx={{
                                    width: "40px",
                                    height: "40px",
                                  }}
                                >
                                  <Icon src={iconSrcHistory} size="small" />
                                </Box>
                              </CustomWidthTooltipHistory>
                            </Box>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center" 
                              sx={{
                                paddingTop: {
                                  xs: "10px",
                                  sm: 0,
                                  md: 0,
                                  lg: 0,
                                  xl: 0,
                                }, 
                                width: {
                                  xs: "100%",
                                  sm: "auto"
                                },
                              }}>
                              {userDetails?.role !== "Patient" && (
                                <FormControl
                                  sx={{
                                    minWidth: "150px",
                                    width: {
                                      xs: "100%",
                                      sm: "0",
                                      md: "0",
                                      lg: "0",
                                      xl: "0",
                                    },
                                  }}
                                >
                                  <Selected
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={popularRangeType}
                                    onChange={(e) =>
                                      setPopularRangeType(e.target.value)
                                    }
                                    sx={{
                                      fontSize: "14px",
                                      "& .MuiSelect-select": {
                                        padding: "10px 15px",
                                        color: "#2C251E",
                                      },
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border:
                                          "1.5px solid rgba(197,165,114,0.6)",
                                        borderRadius: "8px",
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "rgba(197,165,114,0.9)",
                                        },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "rgba(197,165,114,1)",
                                        },
                                      "& .MuiSvgIcon-root": {
                                        color: "#2C251E",
                                      },
                                    }}
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          backgroundColor: "#FEFEFE",
                                          color: "#2C251E",
                                          borderRadius: "10px",
                                          marginTop: "10px",
                                          border:
                                            "1.5px solid rgba(197,165,114,0.6)",

                                          "& .MuiMenuItem-root": {
                                            fontSize: "14px",
                                            borderRadius: "6px",
                                            "&:hover": {
                                              backgroundColor:
                                                "rgba(197,165,114,0.2)",
                                            },
                                          },

                                          "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":
                                            {
                                              backgroundColor:
                                                "rgba(44,37,30,0.9) !important",
                                              color: "#FEFEFE !important",
                                              borderRadius: "0px !important",
                                              "& .MuiListItemButton-root": {
                                                borderRadius: "0px !important",
                                              },
                                            },

                                          "& .MuiList-root .Mui-selected": {
                                            borderRadius: "0px !important",
                                          },

                                          "& .MuiMenuItem-root:first-of-type": {
                                            borderTopLeftRadius: "6px",
                                            borderTopRightRadius: "6px",
                                          },

                                          "& .MuiMenuItem-root:last-of-type": {
                                            borderBottomLeftRadius: "6px",
                                            borderBottomRightRadius: "6px",
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <MenuItem value="7">7 days</MenuItem>
                                    <MenuItem value="30">30 days</MenuItem>
                                    <MenuItem value="365">1 year</MenuItem>
                                    <MenuItem value="all">All time</MenuItem>
                                  </Selected>
                                </FormControl>
                              )}
                            <Box
                              sx={{
                                display: {
                                  xs: "none",
                                  sm: "block",
                                  md: "block",
                                  lg: "block",
                                  xl: "block",
                                }
                              }}>
                              <CustomWidthTooltipHistory
                                title={
                                  userDetails?.role !== "Patient"
                                    ? "The top three most popular booked service."
                                    : "The top three latest completed appointment."
                                }
                                placement="top-end"
                                arrow
                                sx={{
                                  maxWidth: 200,
                                }}
                              >
                                <Box
                                  onMouseEnter={() =>
                                    setIconSrcHistory(
                                      require("../../assets/icons/help_dark.png")
                                    )
                                  }
                                  onMouseLeave={() =>
                                    setIconSrcHistory(
                                      require("../../assets/icons/help.png")
                                    )
                                  }
                                  sx={{
                                    width: "40px",
                                    height: "40px",
                                  }}
                                >
                                  <Icon src={iconSrcHistory} size="small" />
                                </Box>
                              </CustomWidthTooltipHistory>
                              </Box>
                              </Stack>
                          </Stack>
                          
                          {userDetails?.role === "Patient" ? (
                            <>
                            {userDataLoading || treatmentHistoryLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                                  <CircularProgress sx={{ color: "#C5A572" }} />
                                </Box>
                              ) : treatmentHistory.length === 0 ? (
                            <Empty
                                  icon={require("../../assets/icons/document.png")}
                                  header="Oops looks empty here!"
                                  message="Schedule your first appointment and start your journey with us."/>
                              ) : (
                                <Box sx={{ flex: 1, overflowY: "auto", height: "400px" }}>
                                  {treatmentHistory.map((appointment) => {
                                    return (
                                      <List
                                        key={appointment.id}
                                        sx={{
                                          width: "100%",
                                          marginTop: "10px",
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "16px",
                                        }}
                                      >
                                        {/* Appointments */}
                                        <ListItem
                                          sx={{
                                            // border: "1px solid rgba(0, 0, 0, 0.12)",
                                            backgroundColor: "#FDF9F3",
                                            boxShadow:
                                              "0px 2px 5px rgba(0, 0, 0, 0.15)",
                                            borderRadius: "8px",
                                            padding: "20px",
                                          }}
                                        >
                                          {/* Icon */}
                                          <ListItemIcon>
                                            <ListItemAvatar>
                                              <Avatar
                                                sx={{
                                                  backgroundColor: "#558363",
                                                }}
                                              >
                                                <img
                                                  src={check}
                                                  alt="logo"
                                                  className="dashboard-icons"
                                                />
                                              </Avatar>
                                            </ListItemAvatar>
                                          </ListItemIcon>

                                            <Stack
                                              direction={{
                                                xs: "column",
                                                sm: "row",
                                                md: "row",
                                                lg: "row",
                                                xl: "row",
                                              }}
                                              width="100%"
                                              justifyContent="space-between"
                                              >
                                          {/* Left Text  */}
                                          <ListItemText
                                              // Primary Text
                                              primary={
                                                <Typography
                                                  sx={{
                                                    fontSize: "17px",
                                                    color: "#2C251E",
                                                    fontWeight: "600",
                                                  }}
                                                >
                                                  {userDetails?.role !==
                                                  "Patient" ? (
                                                    <>{appointment.name}</>
                                                  ) : (
                                                    <>
                                                      {(() => {
                                                        const servicesArr =
                                                          Array.isArray(
                                                            appointment.services
                                                          )
                                                            ? appointment.services.map(
                                                                (service) =>
                                                                  typeof service ===
                                                                    "object" &&
                                                                  service.name
                                                                    ? service.name
                                                                    : service
                                                              )
                                                            : [
                                                                appointment.services ||
                                                                  "Service",
                                                              ];
                                                        const servicesStr =
                                                          servicesArr.join(
                                                            ", "
                                                          );
                                                        if (
                                                          servicesStr.length >=
                                                          25
                                                        ) {
                                                          const firstCommaIdx =
                                                            servicesStr.indexOf(
                                                              ","
                                                            );
                                                          if (
                                                            firstCommaIdx !== -1
                                                          ) {
                                                            return (
                                                              servicesStr.slice(
                                                                0,
                                                                firstCommaIdx
                                                              ) + ", ..."
                                                            );
                                                          } else {
                                                            return servicesStr.length >
                                                              25
                                                              ? servicesStr.slice(
                                                                  0,
                                                                  17
                                                                ) + "..."
                                                              : servicesStr;
                                                          }
                                                        }
                                                        return servicesStr;
                                                      })()}
                                                    </>
                                                  )}
                                                </Typography>
                                              }
                                             // Secondary Text
                                              secondary={
                                                <Typography
                                                  sx={{
                                                    fontSize: "14px",
                                                    color: "#59544A",
                                                  }}
                                                >
                                                  {appointment.dentist}
                                                </Typography>
                                              }
                                            />

                                          {/* Right Text */}
                                            <ListItemText
                                             primary={
                                               <Typography
                                                  sx={{
                                                    fontWeight: "600",
                                                    fontSize: "17px",
                                                      textAlign: {
                                                        xs: "start",
                                                        sm: "end",
                                                        md: "end",
                                                        lg: "end",
                                                        xl: "end",
                                                      },
                                                    color: "#2C251E",
                                                  }}
                                                >
                                                 {formatDateMMDDYYYY(
                                                    appointment.date ||
                                                      appointment._dateKey
                                                  )}
                                                </Typography>
                                              }
                                              secondary={
                                                <Typography
                                                  sx={{
                                                    fontSize: "14px",
                                                    color: "#59544A",
                                                      textAlign: {
                                                        xs: "start",
                                                        sm: "end",
                                                        md: "end",
                                                        lg: "end",
                                                        xl: "end",
                                                      },
                                                  }}
                                                >
                                                  {(() => {
                                                    const startTime = appointment.startTime || appointment.time || "";
                                                    const endTime = appointment.endTime || "";
                                                    if (startTime && endTime) {
                                                      return `${formatTime12hr(startTime)} - ${formatTime12hr(endTime)}`;
                                                    } else if (startTime) {
                                                      return formatTime12hr(startTime);
                                                    }
                                                    return "N/A";
                                                  })()}
                                                </Typography>
                                              }
                                            />
                                            </Stack>
                                        </ListItem>
                                      </List>
                                    );
                                  })}
                                </Box>
                              )}
                            </>
                          ) : (
                            <Box
                              sx={{
                                    height: "100%",
                              }}>
                              {/* Text */}
                              {popularServices.length === 0 ? (
                                <Empty
                                  icon={require("../../assets/icons/document.png")}
                                  header="Oops looks empty here!"
                                  message="Wait for completed services here to show popular services."/>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    // outer card area keeps its spacing; inner content is forced to the same size
                                    gap: "12px",
                                  }}
                                >
                                  <Box sx={{ flex: 1, overflowY: "auto", height: "100px"}}>
                                    {popularServices.map((service, idx) => {
                                      return (
                                        <List
                                          key={idx}
                                          sx={{
                                            width: "100%",
                                            marginTop: "10px",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "16px",
                                          }}
                                        >
                                          {/* Service Card */}
                                          <ListItem
                                            sx={{
                                              backgroundColor: "#FDF9F3",
                                              boxShadow:
                                                "0px 2px 5px rgba(0, 0, 0, 0.15)",
                                              borderRadius: "8px",
                                              padding: "20px", // match latest appointments card padding
                                               minHeight: { xs: "80px", md: "100px" },        // fixed card height to match appointments
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                            }}
                                          >
                                            {/* Icon */}
                                            <ListItemIcon>
                                              <ListItemAvatar>
                                                <Avatar
                                                  sx={{
                                                    backgroundColor: "#2C251E",
                                                  }}
                                                >
                                                  <img
                                                    src={require("../../assets/tooth.png")}
                                                    alt="logo"
                                                    className="dashboard-icons"
                                                  />
                                                </Avatar>
                                              </ListItemAvatar>
                                            </ListItemIcon>

                                            {/* Left Text  */}
                                            <ListItemText
                                              primary={
                                                <Typography
                                                 sx={{
                                                    fontSize: "17px",
                                                    color: "#2C251E",
                                                   fontWeight: "600",
                                                  }}
                                                >
                                                  {(() => {
                                                    const servicesArr =
                                                      Array.isArray(service.name)
                                                        ? service.name.map(
                                                            (s) =>
                                                              typeof s === "object" &&
                                                              s.name
                                                                ? s.name
                                                                : s
                                                          )
                                                        : [service.name || "Service"];
                                                    const servicesStr =
                                                      servicesArr.join(", ");
                                                    if (servicesStr.length >= 30) {
                                                      const firstCommaIdx =
                                                        servicesStr.indexOf(",");
                                                      if (firstCommaIdx !== -1) {
                                                        return (
                                                          servicesStr.slice(
                                                            0,
                                                            firstCommaIdx
                                                          ) + ", ..."
                                                        );
                                                      } else {
                                                        return servicesStr.length >
                                                          30
                                                          ? servicesStr.slice(
                                                              0,
                                                              17
                                                            ) + "..."
                                                          : servicesStr;
                                                      }
                                                    }
                                                    return servicesStr;
                                                  })()}
                                                </Typography>
                                              }
                                            />

                                            {/* Right Text */}
                                            <ListItemText
                                              primary={
                                                <Typography
                                                  sx={{
                                                    fontSize: "17px",
                                                    color: "#2C251E",
                                                    fontWeight: "300",
                                                    textAlign: "end",
                                                  }}
                                                >
                                                  {service.count}
                                                </Typography>
                                              }
                                            />
                                          </ListItem>
                                        </List>
                                      );
                                    })}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </Box>
                    </Grid>
                    )}
                  </Grid>
                </Box>
                </Grid>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <SplashScreen
        isOpen={showCustomAlert}
        image={alertImage}
        button={alertButton}
        buttonType={alertButtonType}
        destination={alertLink}
        onClose={() => setShowCustomAlert(false)}
      />

      {/* Notifications Modal */}
      <Modal
        isOpen={showNotificationsModal}
        onRequestClose={() => setShowNotificationsModal(false)}
        contentLabel="All Notifications"
                      style={{
                        overlay: {
                          zIndex: 9999,
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          // padding: "16px",
                        },
                        content: {
                          // inset: "unset",
                          border: "none",
                          borderRadius: "16px",
                          background: "#FEFEFE",
                          padding: "24px",
                          width: "80%",
                          maxHeight: "85vh",
                          overflowY: "auto",
                          display: "flex",
                          flexDirection: "column",
                          // justifyContent: "center",
                          // alignItems: "center",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                          // Responsive adjustments
                          transition: "all 0.3s ease",
                          top: "50%",
                          left: "50%",
                          right: "auto",
                          bottom: "auto",
                          // margin: "20px",
                          // // marginRight: "-50%",
                          transform: "translate(-50%, -50%)",
                          // padding: "35px",
                          maxWidth: "600px",
                          minWidth: "180px"
                        },
                      }}
      >
        {/* Close Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <IconButton
            onClick={() => setShowNotificationsModal(false)}
            size="small"
            sx={{
              width: 32,
              height: 32,
              border: "1px solid rgba(44,37,30,0.06)",
              bgcolor: "#fff",
              color: "#2E2D2D",
              fontSize: "18px",
              "&:hover": { bgcolor: "#C5A572", color: "#FEFEFE" },
            }}
            aria-label="close"
          >
            ✕
          </IconButton>
        </Box>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0, mb: 0 }}>
          {/* Title */}
          <Typography
            gutterBottom
            variant="h3"
            component="div"
            sx={{
              fontWeight: "600",
              padding: { xs: "6px 6px", md: "10px 10px" },
              fontSize: { xs: "18px", md: "24px" },
              marginBottom: "0px",
            }}
          >
            All Notifications
          </Typography>

          <Stack direction="row" spacing={2}>
            <Typography
              onClick={(e) => {
                e.stopPropagation();
                notifications.forEach((notification) => {
                  handleMarkAsRead(notification.id);
                });
              }}
              gutterBottom
              variant="h3"
              component="div"
              sx={{
                cursor: "pointer",
                fontWeight: "300",
                padding: { xs: "6px 6px", md: "10px 10px" },
                fontSize: "13px",
                marginBottom: "0px",
                color: "#2C6785",
                ":hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Mark all as read
            </Typography>

            <Typography
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteAllNotifications();
              }}
              gutterBottom
              variant="h3"
              component="div"
              sx={{
                cursor: "pointer",
                fontWeight: "300",
                padding: { xs: "6px 6px", md: "10px 10px" },
                fontSize: "13px",
                marginBottom: "0px",
                color: "#dc3545",
                ":hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Delete All
            </Typography>
          </Stack>
        </Stack>

        {/* Table Container - scrollable area */}
        <TableContainer
          sx={{
            // keep table scroll inside the modal, allow it to shrink on narrow screens
            maxHeight: "60vh",
            minHeight: { xs: "40vh", md: "400px" },
            width: "100%",
            // remove hard minWidth so it fits narrow viewports (e.g. iPhone 12)
            minWidth: "auto",
            overflowX: "auto",
            borderBottom: "1px solid #585858",
            boxSizing: "border-box",
            // remove top spacing so header sits closer to title
            mt: 0,
            pt: 0,
            // ensure the container fits inside modal padding
            px: { xs: 0, md: 0 },
          }}
        >
          <Table aria-label="simple table" stickyHeader>
            {/* Table Head */}
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    borderBottom: "2px solid rgba(89, 84, 74, 1)",
                    color: "rgba(44, 37, 30, .8)",
                    fontWeight: "600",
                    // responsive: use percentage so small screens can wrap
                    width: { xs: "62%", sm: "60%", md: "58%" },
                    minWidth: { xs: "0px", sm: "160px", md: "300px" },
                    whiteSpace: "nowrap",
                    px: { xs: 1, md: 2 },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box>Inbox</Box>
                    <Box
                      sx={{
                        backgroundColor: "#9F4223",
                        width: "25px",
                        height: "20px",
                        marginLeft: "8px",
                        borderRadius: "2px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#FEFEFE",
                        fontSize: 12,
                        px: 0.5,
                      }}
                    >
                      {unreadCount}
                    </Box>
                  </Stack>
                </TableCell>

                {/* Right column reserved for date/time/controls */}
                <TableCell
                  sx={{
                    borderBottom: "2px solid rgba(89, 84, 74, 1)",
                    // use remaining percent, keeps date/time aligned right on small screens
                    width: { xs: "38%", sm: "40%", md: "42%" },
                    minWidth: { xs: "0px", sm: "120px", md: "220px" },
                    textAlign: "right",
                    px: { xs: 1, md: 2 },
                  }}
                />
              </TableRow>
            </TableHead>

            <TableBody>
              {(() => {
                const filtered = notifications
                  .slice()
                  .sort((a, b) => parseNotificationDate(b) - parseNotificationDate(a));

                if (filtered.length === 0) {
                  return (
                    <TableRow>
                      <TableCell sx={{ borderBottom: "none", px: 2, py: 2, textAlign: "center" }}>
                        "No notifications"
                      </TableCell>
                    </TableRow>
                  );
                }

                const start = (notifPage - 1) * NOTIFICATIONS_PAGE_SIZE;
                const paginated = filtered.slice(start, start + NOTIFICATIONS_PAGE_SIZE);

                return paginated.map((notification) => (
                  <TableRow
                    onClick={() => {
                      if (notification.type === 'account-deletion-request') {
                        handleDeletionRequestClick(notification);
                      } else {
                        handleMarkAsRead(notification.id);
                      }
                    }}
                    key={notification.id}
                    sx={{
                      backgroundColor: notification.read ? "#FEFEFE" : "rgba(46, 45, 45, 0.05)",
                      cursor: "pointer",
                      ":hover": { backgroundColor: "rgba(46, 45, 45, 0.08)" },
                    }}
                  >
                    {/* message cell */}
                    <TableCell
                      sx={{
                        borderBottom: "none",
                        pr: { xs: 2, md: 3 },
                        pl: { xs: 1, md: 2 },
                        py: { xs: 1.5, md: 2.5 },
                        whiteSpace: "normal",
                        textAlign: "justify",
                        textAlignLast: "left",
                        textJustify: "inter-word",
                        wordSpacing: "normal",
                        hyphens: "auto",
                        WebkitHyphens: "auto",
                        msHyphens: "auto",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        {notification.type === 'account-deletion-request' && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginTop: "2px" }}>
                            <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                        <Box sx={{ flex: 1 }}>
                          {normalizeMessageDates(notification.message)}
                        </Box>
                      </Stack>
                    </TableCell>

                    {/* right column (date / controls) */}
                    <TableCell
                      sx={{
                        borderBottom: "none",
                        px: { xs: 1, md: 2 },
                        py: { xs: 1.5, md: 2.5 },
                        width: { xs: "140px", sm: "220px", md: "260px" },
                        minWidth: { xs: "120px", sm: "160px", md: "220px" },
                      }}
                    >
                      <Stack
                        direction="row"
                        sx={{ justifyContent: "space-between", alignItems: "center" }}
                      >
                        {/* Date (left) */}
                        <Typography sx={{ fontSize: 12, color: "#59544A", display: { xs: "none", sm: "block" } }}>
                          {formatNotificationDate(notification)}
                        </Typography>

                        {/* Time + unread dot + delete (right) */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: "110px", justifyContent: "flex-end" }}>
                          <Typography sx={{ fontSize: 12, color: "#59544A", minWidth: "70px", textAlign: "right" }}>
                            {formatNotificationTime(notification)}
                          </Typography>

                          <Box
                            sx={{
                              backgroundColor: notification.read ? "transparent" : "#2C6785",
                              width: "10px",
                              height: "10px",
                              borderRadius: "50px",
                              ml: 1,
                            }}
                          />

                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notification.id);
                            }}
                            sx={{
                              color: "#dc3545",
                              padding: "4px",
                              "&:hover": { backgroundColor: "rgba(220, 53, 69, 0.08)" },
                            }}
                            aria-label="Delete notification"
                          >
                            {/* delete svg */}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </IconButton>
                        </Stack>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            paddingTop: "10px",
            paddingBottom: "5px",
            alignContent: "center",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}>
        <Text
          text={"Page " + notifPage + " of " + Math.max(1, Math.ceil(notifications.filter(n => !(n.message?.toLowerCase().includes("welcome") && n.message?.toLowerCase().includes("first notification"))).length / NOTIFICATIONS_PAGE_SIZE))}
          variant="manageListRegular"
        />
        </Box>

        {/* Pagination fixed below the scrollable table (not part of the scrollable area) */}
        <Box sx={{ display: "flex", justifyContent: "center", padding: { xs: "8px 0", md: "12px 0" } }}>
          <Pagination
            count={Math.max(
              1,
              Math.ceil(
                notifications.filter(
                  (notification) =>
                    !(
                      notification.message?.toLowerCase().includes("welcome") &&
                      notification.message?.toLowerCase().includes("first notification")
                    )
                ).length / NOTIFICATIONS_PAGE_SIZE
              )
            )}
            page={notifPage}
            onChange={(e, value) => setNotifPage(value)}
            shape="rounded"
            variant="outlined"
            siblingCount={1}
            boundaryCount={1}
            color="primary"
            sx={{
              "& .MuiPagination-ul": {
                gap: 0,
                columnGap: 0,
                margin: 0,
                padding: 0,
              },
              "& .MuiPaginationItem-root": {
                fontWeight: 600,
                fontSize: "14px",
                borderRadius: "0",
                minWidth: "36px",
                minHeight: "36px",
                border: "1px solid #C5A572",
                backgroundColor: "#FEFEFE",
                margin: 0,
                padding: 0,
                "&.Mui-selected": {
                  backgroundColor: "#2E2D2D",
                },
              },
            }}
          />
        </Box>
      </Modal>

      {/* Appointment Details Modal */}
      <Modal
        isOpen={showAppointmentModal}
        onRequestClose={() => setShowAppointmentModal(false)}
        contentLabel="Appointment Details"
                      style={{
                        overlay: {
                          zIndex: 9999,
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          padding: "16px",
                        },
                        content: {
                          // inset: "unset",
                          border: "none",
                          borderRadius: "16px",
                          background: "#FEFEFE",
                          // padding: "24px",
                          width: "75%",
                          maxHeight: "85vh",
                          overflowY: "auto",
                          display: "flex",
                          flexDirection: "column",
                          // justifyContent: "center",
                          // alignItems: "center",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                          // Responsive adjustments
                          transition: "all 0.3s ease",
                          top: "50%",
                          left: "50%",
                          right: "auto",
                          bottom: "auto",
                          // margin: "20px",
                          // // marginRight: "-50%",
                          transform: "translate(-50%, -50%)",
                          padding: "35px",
                          maxWidth: "600px",
                          minWidth: "180px"
                        },
                      }}
      >
        {selectedAppointment && (
          <Box 
            className="modal-content"
            style={{
              position: 'relative',
              overflow: 'visible',
              height: '100%',
              maxHeight: '75vh',
            }}
          >
            <Box>
              {/* Title + Close Button in one row */}
              <Box
                sx={{
                  position: "relative",
                }}
              >
                <Typography
                  variant="h3"
                  component="div"
                  sx={{
                    fontWeight: "600",
                    padding: "10px 0px",
                    fontSize: "24px",
                    marginBottom: "0px",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  Appointment Details
                </Typography>
                <IconButton
                  onClick={() => setShowAppointmentModal(false)}
                  size="small"
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 32,
                    height: 32,
                    border: "1px solid rgba(44,37,30,0.06)",
                    bgcolor: "#fff",
                    color: "#2E2D2D",
                    fontSize: "18px",
                    "&:hover": { bgcolor: "#C5A572", color: "#FEFEFE" },
                  }}
                  aria-label="close"
                >
                  ✕
                </IconButton>
              </Box>

              {/* Status */}
              <Stack direction="row">
                {/* Label Status */}
                <Typography
                  gutterBottom
                  variant="h3"
                  component="div"
                  sx={{
                    fontWeight: "600",
                    padding: "15px 0",
                    fontSize: "14px",
                    // backgroundColor: "aqua",
                    marginBottom: "0px",
                  }}
                >
                  Status:
                </Typography>

                {/* Status */}
                <Typography
                  gutterBottom
                  variant="h3"
                  component="div"
                  sx={{
                    fontWeight: "600",
                    padding: "15px 10px",
                    fontSize: "14px",
                    // backgroundColor: "aqua",
                    marginBottom: "0px",
                    color:
                      selectedAppointment?.status === "Confirmed"
                        ? "#558363"
                        : selectedAppointment?.status === "Pending"
                        ? "#AD8C60"
                        : "#2C251E",
                  }}
                >
                  {selectedAppointment.status}
                </Typography>
              </Stack>

              <Divider />

              {/* Detail Container */}
              <Box
                textAlign="start"
                sx={{
                  // display: "none",
                  marginTop: "10px",
                  marginBottom: "20px",
                }}
              >
                {/* TextField Container */}
                <Stack direction="column">
                  {/*Grids */}
                  <Grid
                    container
                    rowSpacing={2}
                    columnSpacing={2}
                    sx={{
                      // backgroundColor: "blue",
                      display: "flex",
                    "& .MuiGrid-item": {
                      paddingLeft: 0,
                      paddingRight: 0,
                    },
                    }}
                  >
                    {/* Dentist */}
                    <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }} item>
                      {/* Text */}
                      <Typography variant="h3" sx={styleText}>
                        Dentist
                      </Typography>

                      <TextField
                        // error={Boolean(inputError)}
                        // id={id}
                        // type={type}
                        value={selectedAppointment.dentist}
                        variant="outlined"
                        // required
                        // helperText={errorText}
                        // color={color}
                        FormHelperTextProps={{
                          sx: { color: "error.main" },
                        }}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            borderRadius: "10px",
                            fontSize: "14px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(197, 165, 114, 0.4)",
                              borderWidth: "2px",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(197, 165, 114, 0.4)",
                            },
                          },
                        }}
                        // onChange={handleChange}
                        sx={{
                          width: "100%",
                          borderRadius: "10px",
                        }}
                      />
                    </Grid>

                    {/* Date */}
                    <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }} item>
                      {/* Text */}
                      <Typography variant="h3" sx={styleText}>
                        Date
                      </Typography>

                      <TextField
                        fullWidth
                        // error={Boolean(inputError)}
                        // id={id}
                        // type={type}
                        value={formatDateMMDDYYYY(selectedAppointment.date)}
                        variant="outlined"
                        // required
                        // helperText={errorText}
                        // color={color}
                        FormHelperTextProps={{
                          sx: { color: "error.main" },
                        }}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            borderRadius: "10px",
                            fontSize: "14px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(197, 165, 114, 0.4)",
                              borderWidth: "2px",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(197, 165, 114, 0.4)",
                            },
                          },
                        }}
                        // onChange={handleChange}
                        sx={{
                          width: "100%",
                          borderRadius: "10px",
                        }}
                      />
                    </Grid>

                    {/* Time */}
                    <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }} item>
                      {/* Text */}
                      <Typography variant="h3" sx={styleText}>
                        Time
                      </Typography>

                      <TextField
                        fullWidth
                        value={(function() {
                          const startRaw = selectedAppointment.startTime || selectedAppointment.time || "";
                          const start = formatTime12hr(startRaw) || "N/A";
                          const endRaw =
                            selectedAppointment.endTime ||
                            (selectedAppointment.duration
                              ? addMinutesToTimeStr(startRaw, parseInt(selectedAppointment.duration, 10))
                              : "");
                          const end = endRaw ? formatTime12hr(endRaw) : "";
                          return end ? `${start} - ${end}` : start;
                        })()}
                        variant="outlined"
                        FormHelperTextProps={{
                          sx: { color: "error.main" },
                        }}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            borderRadius: "10px",
                            fontSize: "14px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(197, 165, 114, 0.4)",
                              borderWidth: "2px",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(197, 165, 114, 0.4)",
                            },
                          },
                        }}
                        sx={{
                          width: "100%",
                          borderRadius: "10px",
                        }}
                      />
                    </Grid>

                    {/* Duration */}
                    <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }} item>
                      {/* Text */}
                      <Typography variant="h3" sx={styleText}>
                        Duration
                      </Typography>

                      <TextField
                        fullWidth
                        // error={Boolean(inputError)}
                        // id={id}
                        // type={type}
                        value={
                          selectedAppointment.duration
                            ? `${selectedAppointment.duration} minutes`
                            : "N/A"
                        }
                        variant="outlined"
                        // required
                        // helperText={errorText}
                        // color={color}
                        FormHelperTextProps={{
                          sx: { color: "error.main" },
                        }}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            borderRadius: "10px",
                            fontSize: "14px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(197, 165, 114, 0.4)",
                              borderWidth: "2px",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(197, 165, 114, 0.4)",
                            },
                          },
                        }}
                        // onChange={handleChange}
                        sx={{
                          width: "100%",
                          borderRadius: "10px",
                        }}
                      />
                    </Grid>

                    {/* Services */}
                    <Grid
                      size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                      item
                    >
                      {/* Text */}
                      <Typography variant="h3" sx={styleText}>
                        Service(s)
                      </Typography>

                      <TextField
                        // error={Boolean(inputError)}
                        // id={id}
                        // type={type}
                        value={
                          Array.isArray(selectedAppointment.services)
                            ? selectedAppointment.services
                                .map((service) =>
                                  typeof service === "object" && service.name
                                    ? service.name
                                    : service
                                )
                                .join(", ")
                            : selectedAppointment.services || "Service"
                        }
                        variant="outlined"
                        // required
                        // helperText={errorText}
                        // color={color}
                        FormHelperTextProps={{
                          sx: { color: "error.main" },
                        }}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            borderRadius: "10px",
                            fontSize: "14px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(197, 165, 114, 0.4)",
                              borderWidth: "2px",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(197, 165, 114, 0.4)",
                            },
                          },
                        }}
                        // onChange={handleChange}
                        sx={{
                          width: "100%",
                          borderRadius: "10px",
                        }}
                      ></TextField>
                    </Grid>

                    {/* Insurance Details */}
                    <Grid
                      size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                      item
                    >
                      <Stack
                        direction="row"
                        sx={{
                          justifyContent: "space-between",
                        }}
                      >
                        {/* Text */}
                        <Typography variant="h3" sx={styleText}>
                          Insurance Details
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>

                  {selectedAppointment.hasInsurance ? (
                    <div>
                      {/*Grid */}
                      <Grid
                        container
                        columnSpacing={2}
                        sx={{
                          border: "2px solid rgba(197, 165, 114, 0.4)",
                          borderRadius: "10px",
                          padding: "12px",
                          // backgroundColor: "blue",
                          "& .MuiGrid-item": {
                            display: "flex",
                            justifyContent: "center",
                            // backgroundColor: "blue",
                          },
                        }}
                      >
                        {/* Label Name */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceLabel}>
                            Name
                          </Typography>
                        </Grid>

                        {/* Input Name */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceText}>
                            {selectedAppointment.insuranceDetails?.name ||
                              "Not specified"}
                          </Typography>
                        </Grid>

                        {/* Label Mobile Number */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceLabel}>
                            Mobile Number
                          </Typography>
                        </Grid>

                        {/* Input Mobile Number */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceText}>
                            {selectedAppointment.insuranceDetails
                              ?.contactNumber || "Not specified"}
                          </Typography>
                        </Grid>

                        {/* Label Insurance Company */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceLabel}>
                            Insurance Company
                          </Typography>
                        </Grid>

                        {/* Input Insurance Company */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceText}>
                            {selectedAppointment.insuranceDetails
                              ?.companyName || "Not specified"}
                          </Typography>
                        </Grid>

                        {/* Label Birthdate */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceLabel}>
                            Birthdate
                          </Typography>
                        </Grid>

                        {/* Input Birthdate */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceText}>
                            {selectedAppointment.insuranceDetails?.birthdate ||
                              "Not specified"}
                          </Typography>
                        </Grid>

                        {/* Label HMO Card */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceLabel}>
                            HMO Card
                          </Typography>
                        </Grid>

                        {/* Input HMO Card */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceText}>
                            {selectedAppointment.insuranceDetails?.hmoCard ||
                              "Not specified"}
                          </Typography>
                        </Grid>

                        {/* Label Valid Govt. ID */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceLabel}>
                            Valid Govt. ID
                          </Typography>
                        </Grid>

                        {/* Input Valid Govt. ID */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceText}>
                            {selectedAppointment.insuranceDetails
                              ?.validGovernmentID || "Not specified"}
                          </Typography>
                        </Grid>

                        {/* Label HMO Account No. */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceLabel}>
                            HMO Account No.
                          </Typography>
                        </Grid>

                        {/* Input HMO Account No. */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceText}>
                            {selectedAppointment.insuranceDetails
                              ?.hmoAccountNumber || "Not specified"}
                          </Typography>
                        </Grid>

                        {/* Label Valid Govt. ID No. */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceLabel}>
                            Valid Govt. ID No.
                          </Typography>
                        </Grid>

                        {/* Input Valid Govt. ID No. */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceText}>
                            {selectedAppointment.insuranceDetails
                              ?.validGovernmentIDNumber || "Not specified"}
                          </Typography>
                        </Grid>

                        {/* Label Beneficiary */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceLabel}>
                            Beneficiary
                          </Typography>
                        </Grid>

                        {/* Input Beneficiary */}
                        <Grid size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }} item>
                          {/* Text */}
                          <Typography variant="h3" sx={styleInsuranceText}>
                            {selectedAppointment.insuranceDetails
                              ?.relationship || "Not specified"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </div>
                  ) : (
                    <div>
                      <i>
                        <TextField
                          // error={Boolean(inputError)}
                          // id={id}
                          // type={type}
                          value="No Insurance Provided"
                          variant="outlined"
                          // required
                          // helperText={errorText}
                          // color={color}
                          FormHelperTextProps={{
                            sx: { color: "error.main" },
                          }}
                          InputProps={{
                            readOnly: true,
                            sx: {
                              borderRadius: "10px",
                              fontSize: "14px",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(197, 165, 114, 0.4)",
                                borderWidth: "2px",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(197, 165, 114, 0.4)",
                              },
                            },
                          }}
                          // onChange={handleChange}
                          sx={{
                            width: "100%",
                            borderRadius: "10px",
                          }}
                        ></TextField>
                      </i>

                    </div>
                  )}

                  {insuranceMessage && (
                    <AlertShortMessage
                      type="success"
                      label={insuranceMessage}
                      variant=""
                      src={require("../../assets/check_dark.png")}
                    />
                  )}
                </Stack>
              </Box>

              {/* Cancel Appointment Button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                  paddingTop: "20px",
                  paddingBottom: "0px",
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <Button
                  onClick={handleCancelAppointment}
                  disabled={isCancelAppointmentButtonLoading}
                  sx={{
                    background: isCancelAppointmentButtonLoading ? "#ccc" : "#C5A572",
                    color: "#FDF9F3",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    textTransform: "none",
                    minWidth: "150px",
                    border: "1px solid #C5A572",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    cursor: isCancelAppointmentButtonLoading ? "not-allowed" : "pointer",
                    ":hover": {
                      backgroundColor: isCancelAppointmentButtonLoading ? "#ccc" : "#B8955A",
                    },
                    ":disabled": {
                      backgroundColor: "#ccc",
                      color: "#999",
                      borderColor: "#ccc",
                    },
                  }}
                >
                  {isCancelAppointmentButtonLoading ? (
                    <>
                      <div style={{
                        width: "16px",
                        height: "16px",
                        border: "2px solid #FDF9F3",
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite"
                      }}></div>
                      <span>Cancel Appointment...</span>
                    </>
                  ) : (
                    "Cancel Appointment"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Modal>

      {/* Cancel Appointment Dialog */}
      <Dialog
        open={showCancelAppointmentModal}
        onClose={closeCancelAppointmentModal}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            padding: '0',
            backgroundColor: '#FEFEFE',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '24px 32px 16px 32px',
            borderBottom: '1px solid #e0e0e0',
            position: 'relative',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: '600',
              fontSize: '24px',
              color: '#000000',
              marginBottom: '8px',
            }}
          >
            Cancel Appointment
          </Typography>
          <IconButton
            onClick={closeCancelAppointmentModal}
            sx={{
              position: 'absolute',
              right: '32px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 32,
              height: 32,
              border: '1px solid rgba(44,37,30,0.06)',
              bgcolor: '#fff',
              color: '#2E2D2D',
              fontSize: '18px',
              '&:hover': { bgcolor: '#C5A572', color: '#FEFEFE' },
            }}
          >
            ✕
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: '0 32px',
            paddingBottom: 0,
            '&.MuiDialogContent-root': { paddingTop: '24px !important' },
          }}
        >
          {/* Appointment Details */}
          <Box
            sx={{
              backgroundColor: '#fff',
              border: '2px solid #e0d6c3',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '10px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: '600',
                fontSize: '16px',
                color: '#2C251E',
                marginBottom: '16px',
              }}
            >
              Appointment Details:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: '600', color: '#666' }}>Date:</Typography>
                <Typography>{formatDateMMDDYYYY(selectedAppointment?.date) || 'N/A'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: '600', color: '#666' }}>Time:</Typography>
                <Typography>
                  {(function() {
                    const startRaw = selectedAppointment?.startTime || selectedAppointment?.time || "";
                    if (!startRaw) return 'N/A';
                    const start = formatTime12hr(startRaw);
                    const endRaw =
                      selectedAppointment?.endTime ||
                      (selectedAppointment?.duration
                        ? addMinutesToTimeStr(startRaw, parseInt(selectedAppointment?.duration, 10))
                        : "");
                    const end = endRaw ? formatTime12hr(endRaw) : "";
                    return end ? `${start} - ${end}` : start;
                  })()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: '600', color: '#666' }}>Service:</Typography>
                <Typography>
                  {Array.isArray(selectedAppointment?.services)
                    ? selectedAppointment.services
                        .map((service) =>
                          typeof service === "object" && service.name
                            ? service.name
                            : service
                        )
                        .join(", ")
                    : selectedAppointment?.services || "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: '600', color: '#666' }}>Dentist:</Typography>
                <Typography>{selectedAppointment?.dentist || 'N/A'}</Typography>
              </Box>
            </Box>
          </Box>

          {/* Reason for Cancellation */}
          <Box sx={{ marginBottom: '24px' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: '600',
                fontSize: '16px',
                color: '#2C251E',
                marginBottom: '12px',
              }}
            >
              Reason for Cancellation <span style={{ fontWeight: '400' }}>(Optional):</span>
            </Typography>
            
            <TextField
              multiline
              rows={4}
              fullWidth
              placeholder="Please provide a reason for cancelling this appointment..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              inputProps={{ maxLength: 500 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  '& fieldset': {
                    borderColor: '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C5A572',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#C5A572',
                  },
                },
              }}
            />
            
            <Typography
              sx={{
                fontSize: '12px',
                color: '#999',
                marginTop: '4px',
                textAlign: 'right',
              }}
            >
              {cancelReason.length}/500 characters
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: '0 32px 24px 32px', justifyContent: 'center', gap: '16px', '& .MuiButton-root': { marginLeft: '0 !important' } }}>
          <Button
            onClick={handleConfirmCancelAppointment}
            disabled={isKeepAppointmentLoading || isCancelAppointmentLoading}
            sx={{
              background: (isKeepAppointmentLoading || isCancelAppointmentLoading) ? '#ccc' : '#C5A572',
              color: '#FDF9F3',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              textTransform: 'none',
              minWidth: '180px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              border: '1px solid #C5A572',
              marginLeft: '0 !important',
              ':hover': {
                backgroundColor: (isKeepAppointmentLoading || isCancelAppointmentLoading) ? '#ccc' : '#B8955A',
              },
              ':disabled': {
                backgroundColor: '#ccc',
                color: '#999',
                borderColor: '#ccc',
              },
            }}
          >
            {isCancelAppointmentLoading ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #FDF9F3',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <span>Canceling...</span>
              </>
            ) : (
              'Yes, Cancel Appointment'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack}
        message={message || null}
        background="success"
        onClose={() => setSnack(false)}
      />

      {/* Delete Notification Confirmation Modal */}
      <Dialog
        open={showDeleteNotificationConfirm}
        onClose={() => {
          setShowDeleteNotificationConfirm(false);
          setNotificationToDelete(null);
          setIsDeleteAll(false);
        }}
        sx={{
          zIndex: 10000,
          '& .MuiBackdrop-root': {
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: '#FEFEFE',
            minWidth: '400px',
            maxWidth: '500px',
            position: 'relative',
            zIndex: 10000,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '24px 32px 16px 32px',
            borderBottom: '1px solid #e0e0e0',
            position: 'relative',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: '600',
              fontSize: '24px',
              color: '#59544A',
            }}
          >
            Delete Notification?
          </Typography>
          <IconButton
            onClick={() => {
              setShowDeleteNotificationConfirm(false);
              setNotificationToDelete(null);
              setIsDeleteAll(false);
            }}
            sx={{
              position: 'absolute',
              right: '32px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 32,
              height: 32,
              border: '1px solid rgba(44,37,30,0.06)',
              bgcolor: '#fff',
              color: '#2E2D2D',
              fontSize: '18px',
              '&:hover': { bgcolor: '#C5A572', color: '#FEFEFE' },
            }}
            aria-label="close"
          >
            ✕
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: '0 32px',
            paddingBottom: 0,
            '&.MuiDialogContent-root': { paddingTop: '24px !important' },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#59544A',
              textAlign: 'center',
            }}
          >
            {isDeleteAll 
              ? 'Are you sure you want to delete all notifications? This action cannot be undone.'
              : 'Are you sure you want to delete this notification? This action cannot be undone.'}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            padding: '16px 32px 24px 32px',
            justifyContent: 'center',
            gap: '16px',
          }}
        >
          <Button
            onClick={() => {
              if (isDeleteAll) {
                handleConfirmDeleteAllNotifications();
              } else {
                handleConfirmDeleteNotification();
              }
            }}
            sx={{
              padding: '10px 24px',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: '500',
              color: '#fff',
              backgroundColor: 'rgb(199, 167, 108)',
              '&:hover': {
                backgroundColor: 'rgb(180, 150, 95)',
              },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Medical History Modal - Only shown on first sign-in for patients */}
      {showMedicalHistoryModal && currentUserUid && (
        <Modal
          isOpen={showMedicalHistoryModal}
          onRequestClose={() => setShowMedicalHistoryModal(false)}
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEsc={false}
                      style={{
                        overlay: {
                          zIndex: 9999,
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          padding: "16px",
                        },
                        content: {
                          // inset: "unset",
                          border: "none",
                          borderRadius: "16px",
                          background: "#FEFEFE",
                          // padding: "24px",
                          width: "75%",
                          maxHeight: "85vh",
                          overflowY: "auto",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                          // Responsive adjustments
                          transition: "all 0.3s ease",
                          top: "50%",
                          left: "50%",
                          right: "auto",
                          bottom: "auto",
                          // margin: "20px",
                          marginRight: "-50%",
                          transform: "translate(-50%, -50%)",
                          maxWidth: "700px",
                          minWidth: "250px"
                        },
                      }}
          ariaHideApp={false}
        >
          {/* Fixed Header */}
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >

            {/* Centered title */}
            <h3
              style={{
                textAlign: "center",
                marginBottom: "8px",
                marginTop: "0px",
                fontSize: "24px",
                fontWeight: 600,
                color: "#2C251E",
              }}
            >
              Medical History for {userDetails?.firstName && userDetails?.lastName 
                ? `${userDetails.firstName} ${userDetails.lastName}` 
                : userDetails?.name || "Patient"}
            </h3>
            <Box
              sx={{
                backgroundColor: "rgba(159, 66, 35, .1)",
                borderRadius: "10px",
                padding: "20px",
                border: "1px solid #9F4223"
              }}>
                <Stack
                direction="row"
                spacing={2}
                sx={{
                  alignItems: "center"
                }}
                >
                  <Icon
                    src={require("../../assets/caution.png")}
                    size="small"/>
                    <Typography
                      sx={{
                        color: "#9F4223",
                        fontSize: "14px",
                        fontWeight: {
                          xs: "600",
                          sm: "600",
                          md: "100",
                          lg: "100",
                          xl: "100",
                        },
                      }}>
                      Please disclose any existing or past medical conditions, allergies, medications, or treatments as it may affect your dental care
                    </Typography>
                </Stack>
            </Box>
          </div>

          {/* Search Bar */}
          <div
            style={{
              paddingTop: "16px",
              paddingBottom: "16px",
              width: "100%"
            }}
          >
            <TextField
              placeholder="Search medical history..."
              value={medicalHistorySearchTerm}
              onChange={handleMedicalHistorySearchChange}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  '& fieldset': {
                    borderColor: '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C5A572',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#C5A572',
                  },
                },
              }}
            />
          </div>

          {/* Scrollable Content */}
          <div
            style={{
              flex: 1,
              overflow: "auto",
              minHeight: 0,
              width: "100%"
            }}
          >
            <MedicalHistory
              ref={medicalHistoryRef}
              patientId={currentUserUid}
              searchTerm={medicalHistorySearchTerm}
            />
          </div>

          {/* Fixed Footer with Save Button */}
          <div
            style={{
              flexShrink: 0,
              padding: "16px",
              textAlign: "center",
            }}
          >
            
            <button
              onClick={async () => {
                if (medicalHistoryRef.current) {
                  await medicalHistoryRef.current.save();
                  // Show snackbar
                  setShowMedicalHistorySnackbar(true);
                  // Close the modal after saving
                  // healthHistory will now exist in Firestore, so modal won't show again
                  setShowMedicalHistoryModal(false);
                }
              }}
              style={{
                background: "#C5A572",
                color: "#fff",
                border: "none",
                padding: "10px 28px",
                borderRadius: "6px",
                fontSize: "1.08rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgb(185, 153, 102)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "rgb(197, 165, 114)";
              }}
            >
              <Typography
                sx={{
                  color: "#FEFEFE",
                  fontSize: "14px",
                }}>Save Medical History
                </Typography> 
            </button>
          </div>
        </Modal>
      )}

      {/* Medical History Success Snackbar */}
      <MuiSnackbar
        open={showMedicalHistorySnackbar}
        autoHideDuration={3000}
        onClose={() => {
          setShowMedicalHistorySnackbar(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => {
            setShowMedicalHistorySnackbar(false);
          }}
          severity="success"
          variant="filled"
          icon={false}
          sx={{
            backgroundColor: "#558363",
            color: "#fff",
            fontWeight: 600,
            letterSpacing: 0.2,
            borderRadius: "12px",
            padding: "8px 16px",
            minWidth: "auto",
            width: "auto",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
          }}
        >
          Medical History Saved
        </Alert>
      </MuiSnackbar>

      {/* Deletion Request Modal */}
      <DeletionRequestModal
        isOpen={showDeletionModal}
        onClose={() => setShowDeletionModal(false)}
        requestData={selectedDeletionRequest}
        onApprove={handleApproveDeletion}
        onReject={handleRejectDeletion}
      />

      {/* Service Selection Modal for first-time dentist login */}
      <ServiceSelectionModal
        isOpen={showServiceSelectionModal}
        onComplete={() => {
          setShowServiceSelectionModal(false);
          // Update local state to reflect services have been selected
          setUserDetails(prev => prev ? { ...prev, hasSelectedServices: true } : prev);
        }}
        userUid={auth.currentUser?.uid}
        userName={userDetails?.firstName ? `Dr. ${userDetails.firstName}` : 'Doctor'}
      />

      {/* Success Snackbar */}
      <MuiSnackbar
        open={showSuccessSnackbar}
        autoHideDuration={5000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{
            backgroundColor: "#558363",
            color: "#fff",
            fontWeight: 600,
            letterSpacing: 0.2,
            borderRadius: "12px",
            padding: "8px 16px",
            minWidth: "auto",
            width: "auto",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
          }}
        >
          {snackbarMessage}
        </Alert>
      </MuiSnackbar>

      {/* Error Snackbar */}
      <MuiSnackbar
        open={showErrorSnackbar}
        autoHideDuration={5000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowErrorSnackbar(false)}
          severity="error"
          variant="filled"
          sx={{
            backgroundColor: "#9F4223",
            color: "#fff",
            fontWeight: 600,
            letterSpacing: 0.2,
            borderRadius: "12px",
            padding: "8px 16px",
            minWidth: "auto",
            width: "auto",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
          }}
        >
          {snackbarMessage}
        </Alert>
      </MuiSnackbar>
    </div>
  );
};

export default Dashboard;

